/**
 * Agentic Workflows Helper Functions
 *
 * Utility functions for text processing, parsing, and formatting
 * used across the agentic workflow system.
 */

import {
  GENERIC_HEADING_BLOCKLIST,
  SUPERLATIVE_PATTERNS,
  DEEP_RESEARCH_PREFIX,
  VIDEO_SCRIPT_TRIGGER_PATTERN,
} from './constants';
import type {
  Stage1Result,
  VideoScriptStage1Result,
  VideoScriptSummary,
  VideoScriptClarifyingQuestion,
} from './types';
import type { ChatMessage, WorkflowOutputMetadata } from '../../components/GlobalChatSystem/types';
// Import from the leaf config module (not GlobalChatSystem.tsx) to avoid a
// circular import: helpers.ts -> GlobalChatSystem.tsx -> useGlobalChat.ts
// (registerAllTools at module load). The component re-exports this for other
// callers, but services must use the leaf path.
import { getPublicSystemName } from '../../config/providerCodeNames';
import { sanitizeProviderNames } from '../../config/providerCodeNames';

// ============================================================================
// Video Script Color Constants (Emerald Green Theme)
// ============================================================================

/**
 * Video script workflow uses emerald green color scheme
 * to distinguish from deep research (blue) and translation (purple).
 */
export const VIDEO_SCRIPT_COLORS = {
  // Emerald 700, not 600: the accent is written as words (a title) and behind a white label, and emerald 600 read
  // 3.8:1 in both (2026-09-26, design-system §7a.9); 700 reads 5.5:1.
  accent: '#047857',      // Emerald 700 - primary accent
  accentBg: '#d1fae5',    // Emerald 100 - light background for badges
  border: '#10b981',      // Emerald 500 - border color
  subtitle: '#475569',    // Slate 600 - subtitle text
  muted: '#64748b',       // Slate 500 - muted/helper text
} as const;

// ============================================================================
// Special Message Template Types
// ============================================================================

/**
 * Available color theme names for special message templates.
 */
export type SpecialMessageTheme = 'blue' | 'green' | 'purple' | 'amber' | 'red' | 'custom';

/**
 * Color palette for a special message theme.
 */
export interface SpecialMessageColorPalette {
  /** Primary accent color for titles, icons, borders */
  accent: string;
  /** Light background color for badges and highlights */
  accentBg: string;
  /** Border/accent line color */
  border: string;
  /** Secondary text color (subtitles, descriptions) */
  subtitle: string;
  /** Muted/helper text color */
  muted: string;
}

/**
 * Section types for the template content.
 */
export type TemplateSectionType = 'text' | 'boxed' | 'list' | 'custom';

/**
 * Configuration for a single content section within the template.
 */
export interface TemplateSectionConfig {
  /** Type of section to render */
  type: TemplateSectionType;
  /** Optional title for the section (rendered in accent color) */
  title?: string;
  /** Content: string for text/boxed, string[] for list, HTML string for custom */
  content: string | string[];
  /** Optional styling overrides */
  style?: {
    /** Background color for boxed sections */
    background?: string;
    /** Max height for scrollable boxed sections */
    maxHeight?: string;
    /** Font size override */
    fontSize?: string;
    /** Additional inline styles */
    custom?: string;
  };
}

/**
 * Configuration for an action button in the template.
 */
export interface TemplateButtonConfig {
  /** Button label text */
  label: string;
  /** Unique ID for the button element */
  id: string;
  /** Data attributes to attach to the button (key-value pairs, will be escaped) */
  dataAttributes?: Record<string, string>;
  /** Button background color (defaults to theme accent) */
  background?: string;
  /** Button text color (defaults to white) */
  textColor?: string;
  /** Button style variant: 'primary' uses Stage 3 styling, 'secondary' uses lighter styling */
  variant?: 'primary' | 'secondary';
}

/**
 * Header configuration for the template.
 * Supports flexible icon options: character, SVG, image URL, or base64 image.
 * If no icon is provided, renders as title-only (single line, no badge).
 */
export interface TemplateHeaderConfig {
  /** Title text (required) */
  title: string;
  /** Icon character or emoji to display in the badge (e.g., '?', '✓', '!') */
  icon?: string;
  /** Custom icon as SVG/HTML string (highest priority) */
  iconHtml?: string;
  /** Icon as image URL - renders as circular profile image */
  iconUrl?: string;
  /** Icon as base64 data (just the base64 string, no data: prefix) */
  iconBase64?: string;
  /** MIME type for base64 image (default: 'image/png') */
  iconBase64Type?: string;
}

/**
 * Complete configuration for generating a special message template.
 */
export interface SpecialMessageTemplateConfig {
  /** Header configuration (icon + title) - required */
  header: TemplateHeaderConfig;
  /** Array of content sections */
  sections?: TemplateSectionConfig[];
  /** Array of action buttons */
  buttons?: TemplateButtonConfig[];
  /** Color theme to use (default: 'blue') */
  theme?: SpecialMessageTheme;
  /** Custom color palette (required when theme is 'custom') */
  customColors?: SpecialMessageColorPalette;
  /** Optional helper text at the bottom */
  helperText?: string;
  /** Container margin (default: '4px 0') */
  containerMargin?: string;
  /** Gap between sections (default: '8px') */
  sectionGap?: string;
  /** Whether to show border-top separator above buttons (default: true) */
  showButtonSeparator?: boolean;
  /** Button layout direction: 'vertical' (stacked) or 'horizontal' (side-by-side). Default: 'vertical' */
  buttonLayout?: 'vertical' | 'horizontal';
}

// ============================================================================
// Special Message Theme Palettes
// ============================================================================

/**
 * Predefined color theme palettes for special message templates.
 */
// ── Prest0 platform CTA tokens (2026-08-19 owner directive: special-element
// buttons ride the platform warm ink/gold scheme, never one-off green/gray).
// Values come from the platform special-message palette
// (GlobalChatSystem/styles.ts SPECIAL_MESSAGE_COLORS / SPECIAL_TILE_COLORS):
// deep gold is the platform accent; warm subtitle ink is the neutral action.
export const PREST0_PRIMARY_CTA_BG = '#8A6D2F';
export const PREST0_SECONDARY_CTA_BG = '#6B6459';

export const SPECIAL_MESSAGE_THEMES: Record<Exclude<SpecialMessageTheme, 'custom'>, SpecialMessageColorPalette> = {
  // Prest0 brand scheme (2026-08-12 owner directive, applied here
  // 2026-08-19): the DEFAULT informational theme mirrors the platform's
  // SPECIAL_MESSAGE_COLORS — warm ink + gold, never a one-off blue. The
  // key stays 'blue' because it is the theme NAME every call site and test
  // references; only the palette behind it is platform-toned now.
  blue: {
    accent: '#1C1917',
    accentBg: 'rgba(198, 167, 94, 0.14)',
    border: '#E0D5BC',
    subtitle: '#6B6459',
    muted: 'var(--brand-text-muted)',
  },
  green: {
    // emerald 700: the accent is a title's words and a button's fill behind white; emerald 600 read 3.8:1 in both (§7a.9)
    accent: '#047857',
    accentBg: '#d1fae5',
    border: '#10b981',
    subtitle: '#475569',
    muted: '#64748b',
  },
  purple: {
    accent: '#7c3aed',
    accentBg: '#ede9fe',
    border: '#a78bfa',
    subtitle: '#475569',
    muted: '#64748b',
  },
  amber: {
    // amber 700 for the same reason: amber 600 read 3.2:1 as a word and behind white (§7a.9)
    accent: '#b45309',
    accentBg: '#fef3c7',
    border: '#fbbf24',
    subtitle: '#475569',
    muted: '#64748b',
  },
  red: {
    accent: '#dc2626',
    accentBg: '#fee2e2',
    border: '#f87171',
    subtitle: '#475569',
    muted: '#64748b',
  },
};

// ============================================================================
// Text Processing Helpers
// ============================================================================

/**
 * Strip internal thinking/reasoning blocks from AI responses.
 * Removes <think>, </think>, and similar patterns along with their content.
 *
 * Handles streaming correctly: if an unclosed <think> tag exists (mid-stream),
 * everything from <think> to end-of-string is hidden to prevent thinking content
 * from being visible to the user before the closing tag arrives.
 */
export function stripThinkingBlocks(text: string): string {
  if (!text) return '';

  let cleaned = text;

  // Remove complete <think>...</think> blocks (including multiline)
  cleaned = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, '');

  // Remove unclosed <think> tags and everything after them (in-progress thinking during streaming)
  // This prevents thinking content from being visible before the closing </think> tag arrives
  cleaned = cleaned.replace(/<think>[\s\S]*$/gi, '');

  // Remove standalone </think> tags (orphaned closing tags)
  cleaned = cleaned.replace(/<\/think>/gi, '');

  // Remove any other thinking-style patterns that might appear
  // e.g., [thinking], [/thinking], {thinking}, etc.
  cleaned = cleaned.replace(/\[thinking\][\s\S]*?\[\/thinking\]/gi, '');
  cleaned = cleaned.replace(/\{thinking\}[\s\S]*?\{\/thinking\}/gi, '');

  // Clean up any resulting excessive whitespace/newlines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

  return cleaned.trim();
}

/**
 * Extract all <think>...</think> blocks (raw, without tags).
 */
export function extractThinkBlocks(text: string): string[] {
  if (!text) return [];
  const matches = text.match(/<think>[\s\S]*?<\/think>/gi);
  if (!matches) return [];
  return matches
    .map((m) => m.replace(/<\/?think>/gi, '').trim())
    .filter(Boolean);
}

// ============================================================================
// Title Extraction Helpers
// ============================================================================

/**
 * Check if a heading is generic and should be skipped.
 */
export function isGenericHeading(heading: string): boolean {
  const normalized = heading.toLowerCase().trim().replace(/[*_`#]/g, '');
  return GENERIC_HEADING_BLOCKLIST.some(
    (generic) =>
      normalized === generic ||
      normalized.startsWith(generic + ':') ||
      normalized.startsWith(generic + ' -')
  );
}

/**
 * Remove superlatives and vague descriptors from a title.
 */
export function filterSuperlatives(title: string): string {
  let cleaned = title;
  for (const pattern of SUPERLATIVE_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }
  // Clean up extra spaces and punctuation
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  cleaned = cleaned.replace(/^[:\-–—,\s]+/, '').replace(/[:\-–—,\s]+$/, '');
  return cleaned;
}

/**
 * Generate a concise title from the research content or query.
 * Extracts the first meaningful heading (skipping generic ones) or creates a summary from the query.
 * Filters out superlatives and vague descriptors per user guidelines.
 */
export function generateConciseTitle(content: string, originalQuery: string): string {
  const cleanContent = stripThinkingBlocks(content);
  const trimmedQuery = originalQuery || '';

  // =========================================================================
  // Strategy: Try to derive the title from the TOPIC (query), not from
  // generic document headings like "Legal Research Brief". Only use content
  // headings if they're genuinely descriptive of the subject matter.
  // =========================================================================

  // Step 1: Try to build a topic-based title from the query
  const queryTitle = buildTitleFromQuery(trimmedQuery);

  // Step 2: Look for a content heading that's both non-generic AND
  // topic-specific (contains key words from the query)
  const topicKeywords = extractTopicKeywords(trimmedQuery);
  let bestContentHeading: string | null = null;

  // Extract all headings from the content (H1, H2, H3)
  const headingMatches = cleanContent.matchAll(/^(#{1,3})\s+(.+)$/gm);
  for (const match of headingMatches) {
    const headingText = match[2].trim().replace(/[*_`]/g, '');
    if (headingText.length > 0 && headingText.length <= 200 && !isGenericHeading(headingText)) {
      const filtered = filterSuperlatives(headingText);
      // Prefer headings that actually reference the topic
      if (filtered.length >= 10 && topicKeywords.some(kw => filtered.toLowerCase().includes(kw))) {
        bestContentHeading = filtered;
        break; // Take the first topic-relevant heading
      }
    }
  }

  // Step 3: If no heading matched, try bold title patterns
  if (!bestContentHeading) {
    const boldMatches = cleanContent.matchAll(/^\*\*(.{10,200})\*\*/gm);
    for (const match of boldMatches) {
      const boldText = match[1].trim();
      if (!isGenericHeading(boldText)) {
        const filtered = filterSuperlatives(boldText);
        if (filtered.length >= 10 && topicKeywords.some(kw => filtered.toLowerCase().includes(kw))) {
          bestContentHeading = filtered;
          break;
        }
      }
    }
  }

  // Step 4: Decide — prefer topic-specific content heading if found,
  // otherwise use the query-derived title
  const title = bestContentHeading || queryTitle;

  console.log('[generateConciseTitle] Title decision:', {
    chosen: title,
    contentHeading: bestContentHeading,
    queryTitle,
    topicKeywords,
  });

  return title || 'Legal Research Brief';
}

/**
 * Build a descriptive title from the user's original query.
 * Strips command prefixes and generic filler, preserving the topic.
 */
function buildTitleFromQuery(query: string): string {
  let title = query
    .replace(/^deep research[:\s]*/i, '')
    .replace(/^research[:\s]*/i, '')
    .replace(/ensure to be thorough.*/i, '')
    .replace(/using reasoning\s*=\s*high.*/i, '')
    .replace(/be sure to read.*/i, '')
    .replace(/please\s+(provide|research|analyze|explain)\s*/i, '')
    .replace(/\b(thorough|detailed|comprehensive|analysis|report|document|summary)\b/gi, '')
    .trim();

  title = filterSuperlatives(title);

  // If query is too long, extract the core topic
  if (title.length > 100) {
    const firstSentence = title.match(/^[^.!?]+[.!?]?/);
    if (firstSentence && firstSentence[0].length >= 15 && firstSentence[0].length <= 100) {
      title = firstSentence[0].trim();
    } else {
      const truncated = title.slice(0, 90);
      const lastSpace = truncated.lastIndexOf(' ');
      title = lastSpace > 25 ? truncated.slice(0, lastSpace) : truncated;
    }
  }

  // Capitalize first letter
  if (title.length > 0) {
    title = title.charAt(0).toUpperCase() + title.slice(1);
  }

  // If still nothing meaningful, extract key nouns from query
  if (!title || title.length < 5) {
    const topic = query
      .replace(/[\r\n]+/g, ' ')
      .split(' ')
      .filter((w) => w && w.length > 3)
      .slice(0, 10)
      .join(' ');
    title = topic || 'Legal Research Brief';
  }

  return title.slice(0, 150).trim();
}

/**
 * Extract meaningful topic keywords from a query for matching against headings.
 */
function extractTopicKeywords(query: string): string[] {
  const STOP_WORDS = new Set([
    'the', 'and', 'for', 'from', 'with', 'that', 'this', 'are', 'was', 'were',
    'been', 'being', 'have', 'has', 'had', 'does', 'did', 'will', 'would',
    'could', 'should', 'may', 'might', 'shall', 'can', 'need', 'must',
    'deep', 'research', 'please', 'provide', 'analyze', 'explain',
    'comprehensive', 'thorough', 'detailed', 'analysis', 'report', 'document',
    'ensure', 'using', 'about', 'into', 'under', 'current', 'recent',
  ]);

  return query
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .split(/\s+/)
    .filter(w => w.length >= 3 && !STOP_WORDS.has(w))
    .slice(0, 15);
}

// ============================================================================
// Deep Research Parsing Helpers
// ============================================================================

/**
 * Parse the "deep research [query]" format from user input.
 */
export function parseDeepResearchPrompt(input: string): { isDeepResearch: boolean; query: string } {
  const trimmed = (input || '').trim();
  if (!DEEP_RESEARCH_PREFIX.test(trimmed)) return { isDeepResearch: false, query: '' };
  const query = trimmed.replace(DEEP_RESEARCH_PREFIX, '').trim();
  return { isDeepResearch: true, query };
}

/**
 * CTA shown under Stage-1 quick answers.
 *
 * IMPORTANT: This CTA must only appear when the user explicitly invoked
 * "deep research ...". Deterministically pre-routed/forced deep-research
 * traffic (e.g., intake or legal-intent routing) should not inherit this
 * prompt text.
 */
// copy: deep_research_cta
export const DEEP_RESEARCH_QUICK_ANSWER_CTA_TEXT =
  'if you need more comprehensive analysis, type "deep research [your detailed question]".';

/**
 * Return the quick-answer CTA HTML for explicit deep-research invocations,
 * or an empty string when CTA should be suppressed.
 */
export function buildDeepResearchQuickAnswerCtaHtml(rawUserInput: string): string {
  const { isDeepResearch } = parseDeepResearchPrompt(rawUserInput || '');
  if (!isDeepResearch) return '';
  return `<span style="color:#64748b;font-size:0.85rem;">${DEEP_RESEARCH_QUICK_ANSWER_CTA_TEXT}</span>`;
}

/**
 * Parse the "write a video script [topic]" format from user input.
 * Returns the topic/content after the prefix.
 */
export function parseVideoScriptTrigger(input: string): { isVideoScriptTrigger: boolean; query: string } {
  const trimmed = (input || '').trim();
  if (!VIDEO_SCRIPT_TRIGGER_PATTERN.test(trimmed)) {
    return { isVideoScriptTrigger: false, query: '' };
  }
  const query = trimmed.replace(VIDEO_SCRIPT_TRIGGER_PATTERN, '').trim();
  return { isVideoScriptTrigger: true, query };
}

/**
 * Pattern to detect translate to spanish commands with optional inline content.
 * Matches: "translate to spanish", "translate spanish", "translate to espanol"
 * Does NOT include $ anchor to allow content after the trigger phrase.
 */
const TRANSLATE_SPANISH_PREFIX = /^translate\s*(to\s*)?(spanish|espa[ñn]ol)\s*/i;

/**
 * Parse the "translate to spanish [content]" format from user input.
 * Returns the inline content after the prefix (if any).
 * @deprecated Use parseTranslationPrompt instead for generalized translation support
 */
export function parseTranslateSpanishPrompt(input: string): {
  isTranslateSpanish: boolean;
  inlineContent: string;
} {
  const trimmed = (input || '').trim();
  if (!TRANSLATE_SPANISH_PREFIX.test(trimmed)) {
    return { isTranslateSpanish: false, inlineContent: '' };
  }
  const inlineContent = trimmed.replace(TRANSLATE_SPANISH_PREFIX, '').trim();
  return { isTranslateSpanish: true, inlineContent };
}

// ============================================================================
// Generalized Translation Trigger Pattern
// ============================================================================

/**
 * Pattern to detect generalized translate commands with any target language.
 * Matches: "translate to French", "translate to Japanese", "traduce al español",
 * "traduire en français", "übersetzen nach deutsch", etc.
 * Captures the target language and allows optional inline content after.
 */
// Group 1 is the "to / al / into" connector (INCIDENT-20260812: capturing it
// is what lets the parser tell "translate TO japonés" — where the next word is
// a language by construction — from "translate this technical manual", where
// it is just the next word of a sentence). Group 2 is the language token.
// Connector alternatives are ordered LONGEST-FIRST. Regex alternation is
// first-match-wins, so the old `a|al` order made "traduce al japonés" match
// the bare "a", leaving "l" as the captured language — the helper's own
// documented example returned targetLanguage "L".
const TRANSLATE_PATTERN = /^(?:translate|traduce|traduire|übersetzen|翻译|번역)\s*(?:this\s+)?(into|nach|to|al|en|a|至|으로)?\s*([\p{L}\p{N}]+)\s*/iu;

/**
 * Language name normalization map.
 * Maps common language names and native words to standardized English names.
 */
const LANGUAGE_MAP: Record<string, string> = {
  // English
  'english': 'English',
  // Spanish
  'spanish': 'Spanish',
  'español': 'Spanish',
  'espanol': 'Spanish',
  // French
  'french': 'French',
  'français': 'French',
  'francais': 'French',
  // German
  'german': 'German',
  'deutsch': 'German',
  // Japanese
  'japanese': 'Japanese',
  '日本語': 'Japanese',
  // Chinese
  'chinese': 'Chinese',
  '中文': 'Chinese',
  'mandarin': 'Chinese',
  // Portuguese
  'portuguese': 'Portuguese',
  'português': 'Portuguese',
  'portugues': 'Portuguese',
  // Italian
  'italian': 'Italian',
  'italiano': 'Italian',
  // Korean
  'korean': 'Korean',
  '한국어': 'Korean',
  // Russian
  'russian': 'Russian',
  'русский': 'Russian',
  // Arabic
  'arabic': 'Arabic',
  'العربية': 'Arabic',
  // Dutch
  'dutch': 'Dutch',
  'nederlands': 'Dutch',
  // Polish
  'polish': 'Polish',
  'polski': 'Polish',
  // Turkish
  'turkish': 'Turkish',
  'türkçe': 'Turkish',
  // Vietnamese
  'vietnamese': 'Vietnamese',
  'tiếng việt': 'Vietnamese',
  // Thai
  'thai': 'Thai',
  'ไทย': 'Thai',
  // Hindi
  'hindi': 'Hindi',
  'हिंदी': 'Hindi',
  // Indonesian
  'indonesian': 'Indonesian',
  'bahasa indonesia': 'Indonesian',
  // Swedish
  'swedish': 'Swedish',
  'svenska': 'Swedish',
  // Norwegian
  'norwegian': 'Norwegian',
  'norsk': 'Norwegian',
  // Danish
  'danish': 'Danish',
  'dansk': 'Danish',
  // Finnish
  'finnish': 'Finnish',
  'suomi': 'Finnish',
  // Greek
  'greek': 'Greek',
  'ελληνικά': 'Greek',
  // Hebrew
  'hebrew': 'Hebrew',
  'עברית': 'Hebrew',
  // Czech
  'czech': 'Czech',
  'čeština': 'Czech',
  // Romanian
  'romanian': 'Romanian',
  'română': 'Romanian',
  // Hungarian
  'hungarian': 'Hungarian',
  'magyar': 'Hungarian',
  // Ukrainian
  'ukrainian': 'Ukrainian',
  'українська': 'Ukrainian',
};

/**
 * Capitalize the first letter of a string.
 */
function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Normalize a language name to its standard English form.
 * @param raw - Raw language name from user input
 * @returns Normalized language name (e.g., "español" → "Spanish")
 */
export function normalizeLanguageName(raw: string): string {
  const normalized = raw.toLowerCase().trim();
  return LANGUAGE_MAP[normalized] || capitalizeFirstLetter(raw);
}

/**
 * Parse the "translate to [language] [content]" format from user input.
 * Supports multiple command words and language variations.
 *
 * @param input - User input text
 * @returns Object with isTranslate flag, targetLanguage, and optional inlineContent
 *
 * @example
 * parseTranslationPrompt('translate to French')
 * // { isTranslate: true, targetLanguage: 'French', inlineContent: '' }
 *
 * parseTranslationPrompt('translate to español Hello world')
 * // { isTranslate: true, targetLanguage: 'Spanish', inlineContent: 'Hello world' }
 *
 * parseTranslationPrompt('traduce al japonés')
 * // { isTranslate: true, targetLanguage: 'Japanese', inlineContent: '' }
 */
export function parseTranslationPrompt(input: string): {
  isTranslate: boolean;
  targetLanguage: string;
  inlineContent: string;
} {
  const trimmed = (input || '').trim();
  const match = trimmed.match(TRANSLATE_PATTERN);

  if (!match) {
    return { isTranslate: false, targetLanguage: '', inlineContent: '' };
  }

  const hasLanguageConnector = Boolean(match[1]);
  const rawLanguage = match[2] || '';
  let inlineContent = trimmed.slice(match[0].length).trim();

  // Handle concatenated content (e.g., "englishHola" when no space provided after language)
  const lowerRaw = rawLanguage.toLowerCase();
  let targetLanguage = '';

  if (LANGUAGE_MAP[lowerRaw]) {
    // Direct match - use it
    targetLanguage = normalizeLanguageName(rawLanguage);
  } else {
    // Check if rawLanguage starts with a known language (e.g., "englishHola")
    let foundPrefix = false;
    for (const [langKey, langValue] of Object.entries(LANGUAGE_MAP)) {
      if (lowerRaw.startsWith(langKey) && lowerRaw.length > langKey.length) {
        const remainder = rawLanguage.slice(langKey.length);
        targetLanguage = langValue;
        inlineContent = remainder + (inlineContent ? ' ' + inlineContent : '');
        foundPrefix = true;
        break;
      }
    }
    if (!foundPrefix && hasLanguageConnector) {
      // "translate to <word>" / "traduce al <word>": the connector makes the
      // next token a language by construction, so an unrecognised one is just
      // a language we have no entry for (japonés, Swahili) — not a misparse.
      targetLanguage = normalizeLanguageName(rawLanguage);
    } else if (!foundPrefix) {
      // ── INCIDENT-20260812: the word after the trigger is NOT a language ──
      // TRANSLATE_PATTERN captures whatever word follows "translate [this]",
      // and this branch used to accept it as a language — which reclassified
      // the REST OF THE SENTENCE as content to translate.
      //
      // "Translate this technical manual from Spanish to English. Keep the
      //  section numbering..."
      //   -> language "technical"
      //   -> inline content "manual from Spanish to English. Keep the section
      //      numbering..."
      //
      // A client attached a 2.2 MB, 200-page PDF with exactly that wording and
      // received a 2-page document containing their own sentence, with no
      // error: inline content outranks attachments in translate.ts, so the
      // file was never opened. Every downstream guard we built sits AFTER the
      // point where the document had already been discarded.
      //
      // If the token is not a language, this is prose ("translate this
      // technical manual…"), not the "translate to <lang> <content>" shape —
      // so there is no inline content and no language to report. The
      // orchestrator supplies targetLanguage on the normal path; this parser
      // is only a fallback for when it didn't.
      return { isTranslate: true, targetLanguage: '', inlineContent: '' };
    }
  }

  return { isTranslate: true, targetLanguage, inlineContent };
}

/**
 * Loose translation-intent detector used for the Track C pre-orchestrator
 * router. Unlike `parseTranslationPrompt`, this does NOT require the trigger
 * word to appear at the start of the message — "please translate the
 * following to Spanish" should match.
 *
 * Rules:
 *   1. Message must contain a translate verb anywhere
 *      (translate|translated|translation|traducir|traducción|traduce).
 *   2. Message must contain a target-language clue
 *      (spanish|english|español|inglés or an explicit "to X / al X / en X"
 *      clause).  If ambiguous we return `targetLanguage: 'Spanish'` so
 *      Fernando's primary Spanish-speaking workflow still works.
 *   3. Caller is responsible for the "has a PDF/DOCX attachment" check —
 *      this helper is intent-only.
 */
export function detectTranslationIntentLoose(input: string): {
  isTranslate: boolean;
  targetLanguage: string;
} {
  const trimmed = (input || '').trim();
  if (!trimmed) return { isTranslate: false, targetLanguage: '' };

  const hasTranslateVerb = /\b(translate|translated|translation|traducir|traducci[oó]n|traduce|traduire)\b/i.test(trimmed);
  if (!hasTranslateVerb) return { isTranslate: false, targetLanguage: '' };

  // Explicit "to <lang>" / "al <lang>" / "en <lang>" / "into <lang>".
  //
  // ── INCIDENT-20260812 ──────────────────────────────────────────────────────
  // This used to take the FIRST match of `to|into|al|a|en|nach` and accept
  // whatever word followed as the language. The bare `a` is the English
  // indefinite article, so
  //
  //   "This is a scanned medical file in Spanish. Please translate it to
  //    English and keep the page structure."
  //
  // matched "a scanned" — before ever reaching "to English" — and the target
  // language became "Scanned". The model was asked to translate into a
  // document format, correctly objected that "'Scanned' is a document format,
  // not a language", and 19 paragraphs of that objection were delivered to a
  // client as their translated file.
  //
  // So: look at EVERY connector match and prefer one that resolves to a real
  // language. Only if none does may an unrecognised word be accepted, and then
  // only after a STRONG connector — never after the bare article `a`, which
  // appears in ordinary prose constantly.
  const STRONG_CONNECTOR = /^(?:to|into|al|nach|en)$/i;
  const connectorMatches = Array.from(
    trimmed.matchAll(/\b(to|into|al|a|en|nach)\s+([\p{L}]{3,})\b/giu),
  );
  for (const m of connectorMatches) {
    const lower = m[2].toLowerCase();
    if (LANGUAGE_MAP[lower]) {
      return { isTranslate: true, targetLanguage: LANGUAGE_MAP[lower] };
    }
  }
  for (const m of connectorMatches) {
    if (!STRONG_CONNECTOR.test(m[1])) continue;
    const raw = m[2];
    // Capitalized fallback for languages we have no map entry for (Swahili…).
    return {
      isTranslate: true,
      targetLanguage: raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase(),
    };
  }

  // Bare language mention (e.g. "can you translate this? Spanish please")
  const bareEs = /\b(spanish|espa[ñn]ol)\b/i.test(trimmed);
  const bareEn = /\b(english|ingl[eé]s)\b/i.test(trimmed);
  if (bareEs) return { isTranslate: true, targetLanguage: 'Spanish' };
  if (bareEn) return { isTranslate: true, targetLanguage: 'English' };

  // Translate verb present but no target language — default to Spanish
  // (Fernando's primary Spanish-speaking clientele; safer than guessing nothing).
  return { isTranslate: true, targetLanguage: 'Spanish' };
}

/**
 * Resolve translation intent across a CONVERSATIONAL LEAD-IN.
 *
 * INCIDENT-20260824-C (Fernando, Test 1). `detectTranslationIntentLoose`
 * reads the CURRENT message only, so it needs the translate verb to be in
 * that message. Real users split the request across two turns:
 *
 *   User:      "I need a document translated from Spanish to English."
 *   Prest0n:   "Send it over — which direction, and who is it for?"
 *   User:      "Spanish to English for the file"  + 30-page PDF   <-- no verb
 *
 * That third message carries the attachment and NO translate verb, so the
 * attachment route never fired. The turn fell through to an ordinary chat
 * completion, which wrote a short preface, showed a sample of page 1, and
 * promised "the full 30-page English version is coming to this thread".
 * Nothing was coming — there is no later turn — and the backend deliverable
 * hook packaged that 376-character sample as the user's translation.
 *
 * The intent did not disappear between turns; only our reading of it did.
 * This resolver carries a live translation intent forward onto the turn that
 * finally supplies the document.
 *
 * Deliberately narrow, because a false positive routes an unrelated
 * attachment into the translator. ALL THREE must hold:
 *
 *   1. The current message does NOT already state the intent (if it does,
 *      `detectTranslationIntentLoose` answers and history is never read).
 *   2. The current message names a target language — the shape of an answer
 *      to "which direction?". A bare "here you go" carries nothing forward.
 *   3. A recent turn actually expressed translation intent: a user message
 *      the loose detector matches, or an assistant QUESTION about
 *      translating. Without one, a stray "in English" means nothing.
 *
 * The caller still owns the "has a PDF/DOCX attachment" check, exactly as
 * for `detectTranslationIntentLoose`.
 */
export interface TranslationHistoryTurn {
  /** Chat-surface shape (`ChatMessage`): true = user turn, false = assistant. */
  isUser?: boolean;
  /** Transcript shape: 'user' | 'assistant'. Either field identifies the speaker. */
  role?: 'user' | 'assistant' | string;
  text?: string;
}

/** Is this history entry an assistant turn, under either shape? */
function isAssistantTurn(turn: TranslationHistoryTurn): boolean {
  if (typeof turn?.isUser === 'boolean') return turn.isUser === false;
  return turn?.role === 'assistant';
}

/** How many prior turns can still be carrying a live translation intent. */
export const TRANSLATION_INTENT_LOOKBACK_TURNS = 6;

/**
 * An answer to a clarifying question is short. A long message is a new
 * request in its own right and must state its own intent.
 */
export const TRANSLATION_ANSWER_MAX_CHARS = 240;

/** Did an assistant turn ASK about translating? (a question, not prose) */
function isAssistantTranslationQuestion(text: string): boolean {
  if (!text || !text.includes('?')) return false;
  return /\b(translat\w*|traduc\w*)\b/i.test(text);
}

/**
 * What translation has the CONVERSATION already established, if any?
 *
 * A user turn the loose detector matches, or an assistant QUESTION about
 * translating, both keep the intent alive. The most recent user statement
 * wins the direction — it is the one that named it.
 */
function liveTranslationIntent(
  history: ReadonlyArray<TranslationHistoryTurn> | undefined,
): { isTranslate: boolean; targetLanguage: string } {
  const recent = (history || []).slice(-TRANSLATION_INTENT_LOOKBACK_TURNS);
  let asked = false;
  let targetLanguage = '';
  for (const turn of recent) {
    const text = String(turn?.text || '');
    if (!text.trim()) continue;
    if (isAssistantTurn(turn)) {
      if (isAssistantTranslationQuestion(text)) asked = true;
      continue;
    }
    const intent = detectTranslationIntentLoose(text);
    if (intent.isTranslate) {
      asked = true;
      targetLanguage = intent.targetLanguage || targetLanguage;
    }
  }
  return { isTranslate: asked, targetLanguage };
}

export function resolveTranslationIntentWithHistory(
  currentText: string,
  history: ReadonlyArray<TranslationHistoryTurn> | undefined,
): { isTranslate: boolean; targetLanguage: string; carriedFromHistory: boolean } {
  // 1. The message speaks for itself — unchanged behaviour, no history read.
  const direct = detectTranslationIntentLoose(currentText);
  if (direct.isTranslate) {
    return { ...direct, carriedFromHistory: false };
  }

  const trimmed = (currentText || '').trim();
  if (trimmed.length > TRANSLATION_ANSWER_MAX_CHARS) {
    return { isTranslate: false, targetLanguage: '', carriedFromHistory: false };
  }

  // Resolve what the conversation has already established, once.
  const live = liveTranslationIntent(history);

  // ── An attachment sent with NO WORDS is an answer, by construction ──
  //
  // INCIDENT-20260824-D. The first version of this resolver required the
  // message carrying the file to name a language — a guard against grabbing
  // unrelated attachments. It rejected the most natural way anyone sends a
  // document, and the one Prest0n's own reply invites:
  //
  //   Prest0n: "Send me the document and I'll get it into English."
  //   User:    [attaches the PDF]                        <-- no text at all
  //
  // A message with no words cannot be a NEW request; there is nothing in it
  // to be new. The direction was settled two turns earlier. This is the
  // easiest case in the whole exchange to route, and requiring a language
  // made it the one case that failed — the turn fell through to the general
  // agent loop, which spent 34 minutes producing a Spanish analysis of page 1
  // of a 30-page record the user had asked to have translated into English.
  if (!trimmed) {
    return live.isTranslate
      ? { isTranslate: true, targetLanguage: live.targetLanguage, carriedFromHistory: true }
      : { isTranslate: false, targetLanguage: '', carriedFromHistory: false };
  }

  // 2. Does this message name a target language? Reuse the SAME resolution
  //    the loose detector uses, by asking it about a synthetic phrasing —
  //    so "Spanish to English" resolves to English here exactly as it would
  //    there, and the two can never drift apart.
  const probe = detectTranslationIntentLoose(`translate ${trimmed}`);
  if (!probe.isTranslate) {
    return { isTranslate: false, targetLanguage: '', carriedFromHistory: false };
  }
  // `detectTranslationIntentLoose` defaults to Spanish when a verb is present
  // but no language is. Here the verb is synthetic, so that default would
  // invent an intent out of "here you go". Require a real language mention.
  const namesALanguage =
    /\b(to|into|al|nach|en)\s+([\p{L}]{3,})\b/iu.test(trimmed) ||
    /\b(spanish|espa[nñ]ol|english|ingl[eé]s)\b/i.test(trimmed);
  if (!namesALanguage) {
    return { isTranslate: false, targetLanguage: '', carriedFromHistory: false };
  }

  // 3. Was a translation actually asked for recently?
  if (!live.isTranslate) {
    return { isTranslate: false, targetLanguage: '', carriedFromHistory: false };
  }

  return {
    isTranslate: true,
    targetLanguage: probe.targetLanguage,
    carriedFromHistory: true,
  };
}

/**
 * Pattern to detect format report commands with optional inline content.
 * Matches: "format report", "format for online", "format for audience", "format"
 * Does NOT include $ anchor to allow content after the trigger phrase.
 */
const FORMAT_REPORT_PREFIX = /^format\s*(report|for\s*online|for\s*audience)?\b\s*/i;

/**
 * Parse the "format report [content]" format from user input.
 * Returns the inline content after the prefix (if any).
 */
export function parseFormatReportPrompt(input: string): {
  isFormatReport: boolean;
  inlineContent: string;
} {
  const trimmed = (input || '').trim();
  if (!FORMAT_REPORT_PREFIX.test(trimmed)) {
    return { isFormatReport: false, inlineContent: '' };
  }
  const inlineContent = trimmed.replace(FORMAT_REPORT_PREFIX, '').trim();
  return { isFormatReport: true, inlineContent };
}

/**
 * Only treat the message as a cancel if it is an explicit cancel command (not just containing the word).
 */
export function isCancelCommand(input: string): boolean {
  const t = (input || '').trim().toLowerCase();
  return /^(cancel|stop|nevermind|never mind|no thanks|no thank you|abort|exit|quit)$/.test(t);
}

/**
 * Safely parse the Stage 1 JSON, tolerating code fences and extra text.
 */
export function parseStage1Json(raw: string): Stage1Result | null {
  if (!raw) return null;

  // Strip <think>…</think> blocks before any parsing
  const stripped = raw.replace(/<think>[\s\S]*?<\/think>/g, '').trim();
  if (!stripped) return null;

  const candidates: string[] = [];
  candidates.push(stripped);

  // Remove fenced code blocks if present
  const fenceStripped = stripped.replace(/```(?:json)?/gi, '').replace(/```/g, '').trim();
  if (fenceStripped !== stripped) {
    candidates.push(fenceStripped);
  }

  // Extract JSON object substring if possible
  const firstBrace = stripped.indexOf('{');
  const lastBrace = stripped.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    const objectSlice = stripped.slice(firstBrace, lastBrace + 1);
    candidates.push(objectSlice);
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      const result = {
        requiresDeepResearch: parsed.requiresDeepResearch !== false,
        clarifyingQuestions: parsed.clarifyingQuestions || [],
        initialContext: parsed.initialContext || '',
        simpleAnswer: parsed.simpleAnswer || '',
        reasoning: parsed.reasoning || '',
      };

      // Validation warning for debugging - helps track when AI skips initialContext
      if (!result.initialContext && result.requiresDeepResearch) {
        console.warn(
          '[parseStage1Json] WARNING: initialContext is empty for deep research query. ' +
            'AI should provide substantive preliminary findings.'
        );
      }

      return result;
    } catch {
      // try next candidate
    }
  }

  // =========================================================================
  // Markdown Narrative Fallback
  // =========================================================================
  // When the model ignores the JSON schema and returns a prose/markdown
  // response, extract usable initialContext and clarifyingQuestions from
  // the narrative so the qualification card still renders with real content.
  console.warn('[parseStage1Json] JSON parse failed — attempting markdown narrative fallback');

  return parseStage1FromMarkdown(stripped);
}

/**
 * Extract Stage1Result fields from a markdown/prose response.
 *
 * Strategy:
 * 1. Look for question-like sentences (lines ending with '?')
 * 2. Use the remaining prose as initialContext
 * 3. Always assume requiresDeepResearch = true (the model wouldn't have
 *    written a long narrative if the answer were trivial)
 */
function parseStage1FromMarkdown(text: string): Stage1Result | null {
  // Split into lines for analysis
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;

  // --- Extract clarifying questions ---
  // Look for lines that are questions (end with '?') and appear in a
  // list context or numbered/bulleted pattern.
  const questionCandidates: string[] = [];
  const nonQuestionLines: string[] = [];

  for (const line of lines) {
    // Strip markdown list prefixes: "- ", "* ", "1. ", "1) ", etc.
    const stripped = line.replace(/^(?:[-*•]\s*|\d+[.)]\s*)/, '').trim();

    // A question line: ends with '?' and is at least 25 chars (filters noise)
    if (stripped.endsWith('?') && stripped.length >= 25) {
      // Remove bold markers for cleaner display
      questionCandidates.push(stripped.replace(/\*{1,2}/g, '').trim());
    } else {
      nonQuestionLines.push(line);
    }
  }

  // Build initialContext from the substantive findings first
  // Remove markdown headers (##), bold markers, citation brackets [1][2]
  const contextLines = nonQuestionLines
    .map(l => l
      .replace(/^#{1,4}\s*/, '')        // strip markdown headers
      .replace(/\*{1,2}/g, '')          // strip bold/italic markers
      .replace(/\[\d+\]/g, '')          // strip citation refs [1], [2]
      .trim()
    )
    .filter(l => l.length > 10);        // skip very short lines

  const initialContext = contextLines.join('\n').slice(0, 3000);

  // If we found zero questions but we do have substantive text,
  // return the narrative anyway — the deepResearch tool will generate
  // contextual fallback clarifying questions from the original query.
  if (questionCandidates.length === 0) {
    if (initialContext.length > 100) {
      console.log('[parseStage1FromMarkdown] No questions found but substantive text present — returning context for fallback question generation');
      return {
        requiresDeepResearch: true,
        clarifyingQuestions: [],
        initialContext,
        simpleAnswer: '',
        reasoning: '',
      };
    }
    return null;
  }

  // Take the best 3-5 questions (deduplicate similar ones)
  const finalQuestions = questionCandidates.slice(0, 5);

  console.log(
    `[parseStage1FromMarkdown] Extracted ${finalQuestions.length} questions, ` +
    `${initialContext.length} chars of context from markdown narrative`
  );

  return {
    requiresDeepResearch: true,
    clarifyingQuestions: finalQuestions,
    initialContext,
    simpleAnswer: '',
    reasoning: '',
  };
}

/**
 * Normalize ALL CAPS headers to sentence case.
 * Handles patterns like "HOW QUESTIONS WILL HELP:**", "PRELIMINARY RESEARCH:", etc.
 */
function normalizeHeaderCase(text: string): string {
  // Pattern: 2+ consecutive ALL CAPS words (each word is pure uppercase, 2+ letters)
  // Examples: "HOW QUESTIONS WILL HELP", "PRELIMINARY RESEARCH", "**KEY FINDINGS**"
  // Does NOT match sentence case like "This document" (has lowercase letters)
  const allCapsHeaderPattern = /(\*{0,2})([A-Z]{2,}(?:\s+[A-Z]{2,})+)(\*{0,2})\s*:?\s*/g;

  return text.replace(allCapsHeaderPattern, (match, leadingStars, capsText, trailingStars) => {
    const normalized = capsText.trim().toLowerCase();
    const sentenceCase = normalized.charAt(0).toUpperCase() + normalized.slice(1);

    if (leadingStars === '**' || trailingStars === '**') {
      return `**${sentenceCase}:** `;
    }
    return `${sentenceCase}: `;
  });
}

/**
 * Parse initialContext to extract preliminary findings and "how questions help" sections.
 * Handles both new format (with ---SEPARATOR---) and legacy format (with ALL CAPS headers).
 */
export function parseInitialContextSections(initialContext: string): {
  preliminaryFindings: string;
  howQuestionsHelp: string | null;
} | null {
  if (!initialContext?.trim()) return null;

  let cleaned = stripThinkingBlocks(initialContext).trim();

  // Remove "PART 1:" / "PART 2:" prefixes (legacy format)
  cleaned = cleaned.replace(/\*{0,2}PART\s+\d+\s*:?\s*\*{0,2}/gi, '');

  // Try separator format first
  if (cleaned.includes('---SEPARATOR---')) {
    const parts = cleaned.split('---SEPARATOR---');
    return {
      preliminaryFindings: normalizeHeaderCase(parts[0].trim())
        .replace(/^(\*{0,2})preliminary\s+research(\*{0,2})\s*:?\s*/i, '')
        .trim(),
      howQuestionsHelp: parts[1]
        ? normalizeHeaderCase(parts[1].trim())
            .replace(/^(\*{0,2})how\s+questions\s+will\s+help(\*{0,2})\s*:?\s*/i, '')
            .trim()
        : null,
    };
  }

  // Step 1 (v8): Make primary regex more flexible to match various phrasings
  // Matches: "HOW QUESTIONS WILL HELP", "How Questions Will Help", "HOW YOUR ANSWERS WILL HELP", etc.
  const howQuestionsPattern = /(\*{0,2})(HOW\s+(?:QUESTIONS|YOUR\s+ANSWERS?)\s+WILL\s+HELP|PART\s*2\s*:?\s*HOW)(\*{0,2})\s*:?\s*/i;
  const match = cleaned.match(howQuestionsPattern);

  if (match && match.index !== undefined) {
    return {
      preliminaryFindings: normalizeHeaderCase(cleaned.slice(0, match.index).trim())
        .replace(/^(\*{0,2})preliminary\s+research(\*{0,2})\s*:?\s*/i, '')
        .trim(),
      howQuestionsHelp: normalizeHeaderCase(cleaned.slice(match.index + match[0].length).trim()).trim(),
    };
  }

  // Step 2 (v8): Secondary pattern check - look for looser "how" phrases before giving up
  const looseHowPattern = /\n\s*(?:\*{0,2})(?:how\s+(?:your\s+)?(?:answers?|questions?)\s+(?:will\s+)?help|to\s+tailor\s+this\s+research)(?:\*{0,2})\s*:?\s*/i;
  const looseMatch = cleaned.match(looseHowPattern);
  if (looseMatch && looseMatch.index !== undefined) {
    return {
      preliminaryFindings: normalizeHeaderCase(cleaned.slice(0, looseMatch.index).trim())
        .replace(/^(\*{0,2})preliminary\s+research(\*{0,2})\s*:?\s*/i, '')
        .trim(),
      howQuestionsHelp: normalizeHeaderCase(cleaned.slice(looseMatch.index + looseMatch[0].length).trim()).trim() || null,
    };
  }

  // Build result for fallback case
  const result = {
    preliminaryFindings: normalizeHeaderCase(cleaned)
      .replace(/^(\*{0,2})preliminary\s+research(\*{0,2})\s*:?\s*/i, '')
      .trim(),
    howQuestionsHelp: null as string | null,
  };

  // Step 3 (v8): Content validation - if preliminaryFindings still contains "how...help" pattern, extract it
  if (result.howQuestionsHelp === null && result.preliminaryFindings) {
    const stillContainsHow = /how\s+(?:your\s+)?(?:answers?|questions?)\s+(?:will\s+)?help/i.test(result.preliminaryFindings);
    if (stillContainsHow) {
      const extractMatch = result.preliminaryFindings.match(/(.+?)(?:\n\s*)(how\s+(?:your\s+)?(?:answers?|questions?)\s+(?:will\s+)?help\s*:?\s*)([\s\S]*)/i);
      if (extractMatch) {
        result.preliminaryFindings = extractMatch[1].trim();
        result.howQuestionsHelp = extractMatch[3].trim() || null;
      }
    }
  }

  return result;
}

/**
 * Convert markdown to HTML for display in initial findings.
 * Handles: headings, bold, italic, lists, horizontal rules, and newlines.
 * Also normalizes ALL CAPS headers to sentence case for consistency.
 */
function convertMarkdownToHtml(text: string): string {
  return text
    // Normalize ALL CAPS headers to sentence case (2+ uppercase words only)
    // Does NOT match sentence case like "This document" (has lowercase letters)
    .replace(/(\*{0,2})([A-Z]{2,}(?:\s+[A-Z]{2,})+)(\*{0,2})\s*:?\s*/g, (match, lead, caps, trail) => {
      const normalized = caps.trim().toLowerCase();
      const sentenceCase = normalized.charAt(0).toUpperCase() + normalized.slice(1);
      if (lead === '**' || trail === '**') return `**${sentenceCase}:** `;
      return `${sentenceCase}: `;
    })
    // Convert ### headings to bold with spacing (before ## and #)
    .replace(/^###\s+(.+)$/gm, '<br/><strong>$1</strong><br/>')
    // Convert ## headings to bold with spacing
    .replace(/^##\s+(.+)$/gm, '<br/><strong style="font-size:1em;">$1</strong><br/>')
    // Convert # headings to bold with spacing
    .replace(/^#\s+(.+)$/gm, '<br/><strong style="font-size:1.05em;">$1</strong><br/>')
    // Convert horizontal rules to spacing
    .replace(/^---+$/gm, '<br/>')
    // Convert **bold** to <strong>
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Convert *italic* to <em> (single asterisks, but not if part of list)
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
    // Convert list items starting with - to bullet points
    .replace(/^-\s+(.+)$/gm, '• $1')
    // Convert newlines to <br/>
    .replace(/\n/g, '<br/>')
    // Clean up excessive <br/> tags
    .replace(/(<br\/>){3,}/g, '<br/><br/>');
}

/**
 * Format Stage 1 initial findings for user-friendly display.
 * Separates preliminary findings and "how questions will help" into distinct visual sections.
 *
 * @param _reasoning - DEPRECATED: Kept for backward compatibility, NOT displayed
 * @param initialContext - The initial context from Stage 1 response
 * @param _displayLimit - DEPRECATED: No longer used
 * @returns HTML string with formatted sections, or empty string if no content
 */
export function formatStage1InitialFindings(
  _reasoning: string,
  initialContext: string,
  _displayLimit?: number,
  language: 'en' | 'es' = 'en',
): string {
  const parsed = parseInitialContextSections(initialContext);

  if (!parsed || !parsed.preliminaryFindings) {
    return '';
  }

  const { preliminaryFindings } = parsed;
  const findingsHtml = convertMarkdownToHtml(preliminaryFindings);
  const findingsLabel = language === 'es' ? 'hallazgos iniciales:' : 'initial findings:';

  // Preliminary findings box (blue-gray theme)
  // Note: HTML on single line to avoid template literal whitespace in output
  // ANSWER-FIRST (owner directive 2026-08-20): the "how your answers will
  // help" block is removed on every surface — findings are the whole body.
  // parseInitialContextSections still splits the section off so legacy model
  // outputs that emit it don't leak the heading into the findings box.
  // NO PREVIEW WINDOWS IN ACTION TILES (owner directive 2026-08-29), and
  // house palette only: this was a slate-blue inset box (#f1f5f9/#334155)
  // with a legacy-blue heading (#1e40af — a hex normalizeLegacyCtaColors
  // rewrites) nested inside the qualification tile. Now a plain labelled
  // block in warm ink, no background, no border.
  return `<div style="flex-shrink:0;font-size:0.9rem;color:#1C1917;line-height:1.55;margin-bottom:8px;"><strong style="color:#6B6459;">${findingsLabel}</strong><br/>${findingsHtml}</div>`;
}

// ============================================================================
// HTML Formatting Helpers
// ============================================================================

/**
 * Format research content to styled HTML for display in chat.
 */
export function formatResearchToHtml(text: string): string {
  // Strip thinking blocks before processing
  const cleanedText = stripThinkingBlocks(text);
  const lines = (cleanedText || '').split('\n');
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cleanText = (s: string) =>
    (s || '')
      .replace(/[*_]{1,3}/g, '') // strip markdown emphasis markers
      .replace(/`+/g, '') // strip backticks
      .replace(/^\s*["""']+/, '') // leading quotes
      .replace(/["""']+\s*$/, '') // trailing quotes
      .replace(/\s+\[/g, ' [') // tidy bracket spacing
      .replace(/\s{2,}/g, ' ') // collapse excess whitespace
      .trim();
  const formatCitations = (s: string) =>
    s.replace(/\[(\d+)\]/g, (_m, n) => `<sup style="font-size:12px;">[${n}]</sup>`);
  const linkify = (s: string) =>
    s
      // markdown links
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        (_m, label, url) =>
          `<a href="${escape(url)}" target="_blank" rel="noopener noreferrer" style="color:#0f4fff;text-decoration:none;font-weight:600;">${escape(
            cleanText(label)
          )}</a>`
      )
      // bare urls
      .replace(
        /(?<!["'()])\b(https?:\/\/[^\s<]+)/g,
        (_m, url) =>
          `<a href="${escape(url)}" target="_blank" rel="noopener noreferrer" style="color:#0f4fff;text-decoration:none;font-weight:600;">${escape(
            url
          )}</a>`
      );
  const blocks: string[] = [];
  let inList = false;
  let tableBuffer: string[][] | null = null;
  const closeList = () => {
    if (inList) {
      blocks.push('</ul>');
      inList = false;
    }
  };
  const flushTable = () => {
    if (!tableBuffer || tableBuffer.length === 0) return;
    const rows = tableBuffer;
    tableBuffer = null;
    const renderRow = (cells: string[], header = false) =>
      `<tr>${cells
        .map(
          (c) =>
            `<${header ? 'th' : 'td'} style="padding:8px 10px;border:1px solid #e5e7eb;text-align:left;font-weight:${
              header ? 700 : 400
            };">` +
            `${formatCitations(linkify(escape(cleanText(c))))}</${header ? 'th' : 'td'}>`
        )
        .join('')}</tr>`;
    const headerRow = rows[0];
    const bodyRows = rows.slice(1).filter((r) => r.some((c) => c.trim()));
    const htmlTable = `
      <div style="margin:4px 0 10px 0; overflow:auto;">
        <table style="border-collapse:collapse; min-width:60%; font-size:14px; color:#0f172a;">
          ${renderRow(headerRow, true)}
          ${bodyRows.map((r) => renderRow(r)).join('')}
        </table>
      </div>`;
    blocks.push(htmlTable);
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      closeList();
      flushTable();
      return;
    }
    // Table detection: lines starting with '|' become rows until a non-table line
    if (line.startsWith('|')) {
      closeList();
      const cells = line
        .split('|')
        .map((c) => c.trim())
        .filter((c) => c.length > 0 && !/^[-]+$/.test(c));
      if (!tableBuffer) tableBuffer = [];
      if (cells.length) {
        tableBuffer.push(cells);
      }
      return;
    } else if (tableBuffer) {
      // end of table
      flushTable();
    }

    // h6 header (must check before h5, h4, h3, h2, h1 since longer prefixes match shorter ones)
    if (line.startsWith('###### ')) {
      closeList();
      blocks.push(
        `<h6 style="margin:4px 0 2px 0;font-size:13px;font-weight:600;color:#1e293b;">${formatCitations(
          linkify(escape(cleanText(line.slice(7))))
        )}</h6>`
      );
    } else if (line.startsWith('##### ')) {
      closeList();
      blocks.push(
        `<h5 style="margin:5px 0 2px 0;font-size:13.5px;font-weight:600;color:#0f172a;">${formatCitations(
          linkify(escape(cleanText(line.slice(6))))
        )}</h5>`
      );
    } else if (line.startsWith('#### ')) {
      closeList();
      blocks.push(
        `<h4 style="margin:6px 0 2px 0;font-size:14px;font-weight:700;color:#020617;">${formatCitations(
          linkify(escape(cleanText(line.slice(5))))
        )}</h4>`
      );
    } else if (line.startsWith('### ')) {
      closeList();
      blocks.push(
        `<h3 style="margin:8px 0 2px 0;font-size:15px;font-weight:700;color:#020617;letter-spacing:0.12px;">${formatCitations(
          linkify(escape(cleanText(line.slice(4))))
        )}</h3>`
      );
    } else if (line.startsWith('## ')) {
      closeList();
      blocks.push(
        `<h2 style="margin:12px 0 4px 0;font-size:16px;font-weight:800;color:#000000;letter-spacing:0.15px;border-bottom:1px solid #1e3a5f;padding-bottom:4px;">${formatCitations(
          linkify(escape(cleanText(line.slice(3))))
        )}</h2>`
      );
    } else if (line.startsWith('# ')) {
      closeList();
      blocks.push(
        `<h1 style="margin:14px 0 5px 0;font-size:18px;font-weight:800;color:#000000;letter-spacing:0.2px;">${formatCitations(
          linkify(escape(cleanText(line.slice(2))))
        )}</h1>`
      );
    } else if (/^[-*]\s+/.test(line)) {
      if (!inList) {
        blocks.push(
          '<ul style="margin:0 0 6px 14px; padding:0; line-height:1.54; display:flex; flex-direction:column; gap:2px;">'
        );
        inList = true;
      }
      // Extract content after bullet
      let itemContent = line.replace(/^[-*]\s+/, '');

      // Check for checkbox syntax: [ ] or [x] or [X]
      const uncheckedMatch = itemContent.match(/^\[\s*\]\s+(.*)$/);
      const checkedMatch = itemContent.match(/^\[[xX]\]\s+(.*)$/);

      let checkboxHtml = '';
      if (uncheckedMatch) {
        itemContent = uncheckedMatch[1];
        checkboxHtml = '<span style="display:inline-block;width:16px;height:16px;border:2px solid #94a3b8;border-radius:3px;margin-right:8px;flex-shrink:0;"></span>';
      } else if (checkedMatch) {
        itemContent = checkedMatch[1];
        checkboxHtml = '<span style="display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;background:#7c3aed;border:2px solid #7c3aed;border-radius:3px;margin-right:8px;flex-shrink:0;color:white;font-size:11px;font-weight:bold;">✓</span>';
      }

      if (checkboxHtml) {
        blocks.push(
          `<li style="display:flex;align-items:flex-start;">${checkboxHtml}<span>${formatCitations(linkify(escape(cleanText(itemContent))))}</span></li>`
        );
      } else {
        blocks.push(
          `<li>${formatCitations(linkify(escape(cleanText(itemContent))))}</li>`
        );
      }
    } else {
      closeList();
      blocks.push(
        `<p style="margin:0 0 6px 0; white-space:pre-wrap;">${formatCitations(
          linkify(escape(cleanText(line)))
        )}</p>`
      );
    }
  });
  closeList();
  flushTable();

  return `<article style="font-size:15px; line-height:1.58; display:flex; flex-direction:column; gap:6px; color:#0f172a; font-family: 'Inter', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif; max-width:880px; background:#fff; border:1px solid #1e3a5f; border-radius:10px; padding:18px 22px; box-shadow:0 1px 4px rgba(0,0,0,0.08);">${blocks.join(
    '\n'
  )}</article>`;
}

// ============================================================================
// References Section Validation & Injection
// ============================================================================

/**
 * Patterns to detect an existing references/bibliography section header.
 */
const REFERENCE_SECTION_PATTERNS = [
  /^#{1,3}\s*(references|bibliography|source\s*citations|sources?\s*cited|works\s*cited|complete\s*source\s*citations)/im,
  /^(references|bibliography|source\s*citations|sources?\s*cited|works\s*cited|complete\s*source\s*citations)\s*$/im,
  /^\*{1,2}(references|bibliography|source\s*citations|sources?\s*cited|works\s*cited)\*{1,2}\s*$/im,
  /^#{1,3}\s*XV\.\s*(complete\s*source\s*citations|references|bibliography)/im,
];

/**
 * Detect whether the content already contains a references/bibliography section.
 * Returns the index of the section header if found, or -1 if not found.
 */
export function findReferencesSectionIndex(content: string): number {
  for (const pattern of REFERENCE_SECTION_PATTERNS) {
    const match = content.match(pattern);
    if (match && match.index !== undefined) {
      return match.index;
    }
  }
  return -1;
}

/**
 * Extract all URLs from the content text (both markdown links and bare URLs).
 * Returns deduplicated array of { label, url } pairs.
 */
export function extractUrlsFromContent(content: string): Array<{ label: string; url: string }> {
  const results: Array<{ label: string; url: string }> = [];
  const seen = new Set<string>();

  // Extract markdown links: [label](url)
  const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let match;
  while ((match = mdLinkRegex.exec(content)) !== null) {
    const url = match[2];
    if (!seen.has(url)) {
      seen.add(url);
      results.push({ label: match[1].trim(), url });
    }
  }

  // Extract bare URLs not already captured
  const bareUrlRegex = /(?<!\]\()(?<!\()(https?:\/\/[^\s)<\]]+)/g;
  while ((match = bareUrlRegex.exec(content)) !== null) {
    const url = match[1];
    if (!seen.has(url)) {
      seen.add(url);
      try {
        const domain = new URL(url).hostname.replace('www.', '');
        results.push({ label: domain, url });
      } catch {
        results.push({ label: url, url });
      }
    }
  }

  return results;
}

/**
 * Ensure content includes a References section. If one is missing,
 * construct it from inline citations, URLs in the text, and the
 * Perplexity citations array.
 *
 * @param content - The raw research content from Perplexity
 * @param perplexityCitations - Optional citations[] array from the Perplexity API response
 * @returns Content with a guaranteed References section appended (if missing)
 */
export function ensureReferencesSection(
  content: string,
  perplexityCitations?: string[]
): string {
  // Build references from available sources
  const inlineUrls = extractUrlsFromContent(content);
  const citationUrls = (perplexityCitations || []).filter(
    (url) => url && url.startsWith('http')
  );

  // Merge: inline URLs first, then Perplexity citations not already included
  const seen = new Set(inlineUrls.map((u) => u.url));
  const allRefs = [...inlineUrls];

  for (const url of citationUrls) {
    if (!seen.has(url)) {
      seen.add(url);
      try {
        const domain = new URL(url).hostname.replace('www.', '');
        allRefs.push({ label: domain, url });
      } catch {
        allRefs.push({ label: url, url });
      }
    }
  }

  const existingIdx = findReferencesSectionIndex(content);

  // MT-QA-BUG-008 (2026-05-08 G2 regression): if a References section
  // already exists but looks empty or is only the "No external sources
  // were cited" stub AND we have real citations to inject, rewrite the
  // section instead of leaving the misleading stub in place. The pre-fix
  // behavior short-circuited on the presence of the header alone, which
  // caused PDFs to ship bracket citations ([25], [28]) paired with an
  // empty References list.
  if (existingIdx !== -1) {
    const existingTail = content.slice(existingIdx);
    const bodyAfterHeader = existingTail
      .replace(/^[^\n]*\n/, '') // strip the header line itself
      .trim();
    const looksEmpty = bodyAfterHeader.length === 0;
    const looksLikeStub =
      /^no external sources were cited/i.test(bodyAfterHeader) ||
      /^no\s+(?:sources|citations)\s+(?:were\s+)?(?:cited|provided)/i.test(bodyAfterHeader) ||
      (bodyAfterHeader.length < 80 && !/https?:\/\//.test(bodyAfterHeader));

    if ((looksEmpty || looksLikeStub) && allRefs.length > 0) {
      const before = content.slice(0, existingIdx).replace(/\s+$/, '');
      let refsSection = '\n\n## References\n\n';
      allRefs.forEach((ref, idx) => {
        refsSection += `${idx + 1}. [${ref.label}](${ref.url})\n`;
      });
      return `${before}${refsSection}`;
    }

    return content;
  }

  if (allRefs.length === 0) {
    return content + '\n\n## References\n\nNo external sources were cited in this report.\n';
  }

  // Build markdown references section
  let refsSection = '\n\n## References\n\n';
  allRefs.forEach((ref, idx) => {
    refsSection += `${idx + 1}. [${ref.label}](${ref.url})\n`;
  });

  return content + refsSection;
}

/**
 * 2026-08-15 citation-cutoff fix — deterministic repair for a TRUNCATED
 * references list.
 *
 * ensureReferencesSection deliberately short-circuits when a References
 * section already exists and looks non-empty (>= 80 chars or contains a URL).
 * That gate is correct for intact reports, but when stage 3 stops on
 * finish_reason 'length' the References section — the mandated LAST section —
 * is the part that gets cut mid-list. The result passes the "non-empty" test
 * and shipped exactly as truncated, discarding the authoritative Perplexity
 * citations[] entirely.
 *
 * This helper APPENDS every citation URL that is missing from the existing
 * (possibly truncated) references section, continuing the numbering. Existing
 * entries are never rewritten or removed — the repair is purely additive, so
 * an intact report passes through unchanged.
 *
 * Pure function — no I/O, unit-testable.
 */
export function appendMissingCitationsToReferences(
  content: string,
  citations: string[] | undefined,
): { content: string; appended: number } {
  const citationUrls = (citations || []).filter(
    (u) => typeof u === 'string' && u.startsWith('http')
  );
  if (citationUrls.length === 0) return { content, appended: 0 };

  const refsIdx = findReferencesSectionIndex(content);
  if (refsIdx === -1) {
    // No section at all — delegate to the existing builder.
    const rebuilt = ensureReferencesSection(content, citationUrls);
    return { content: rebuilt, appended: rebuilt === content ? 0 : citationUrls.length };
  }

  const refsTail = content.slice(refsIdx);
  // URLs already present anywhere in the section survive as-is.
  const present = new Set(
    Array.from(refsTail.matchAll(/https?:\/\/[^\s)\]<>"']+/g)).map((m) =>
      m[0].replace(/[).,;:\]]+$/, '')
    )
  );
  const missing = citationUrls.filter((u) => !present.has(u.replace(/[).,;:\]]+$/, '')));
  if (missing.length === 0) return { content, appended: 0 };

  // Continue numbering after the highest existing "N." or "[N]" entry.
  let maxNum = 0;
  for (const m of refsTail.matchAll(/^\s*(?:\[(\d+)\]|(\d+)\.)\s+/gm)) {
    const n = parseInt(m[1] || m[2], 10);
    if (Number.isFinite(n) && n > maxNum) maxNum = n;
  }

  let addition = '';
  missing.forEach((url, i) => {
    let label = url;
    try {
      label = new URL(url).hostname.replace('www.', '');
    } catch {
      // keep raw url as label
    }
    addition += `${maxNum + i + 1}. [${label}](${url})\n`;
  });

  const trimmed = content.replace(/\s+$/, '');
  return { content: `${trimmed}\n${addition}`, appended: missing.length };
}

/**
 * 2026-08-15 citation-cutoff guard for the format-report pass.
 *
 * The formatter re-emits the whole report; if its output hit the token cap or
 * otherwise dropped sources, shipping it silently loses citations (the tail of
 * the document is the References section). Decide deterministically whether the
 * PRE-format content must be used instead.
 *
 * Pure function — no I/O, unit-testable.
 */
export function evaluateFormattedCitationLoss(
  original: string,
  formatted: string,
  formatterTruncated?: boolean,
): { useOriginal: boolean; reason?: string } {
  if (formatterTruncated) {
    return {
      useOriginal: true,
      reason: 'formatter output hit its token cap (stop_reason max_tokens/length)',
    };
  }
  const origRefsIdx = findReferencesSectionIndex(original);
  if (origRefsIdx === -1) return { useOriginal: false };

  if (findReferencesSectionIndex(formatted) === -1) {
    return { useOriginal: true, reason: 'formatted output lost the References section' };
  }

  const urlSet = (s: string): Set<string> =>
    new Set(
      Array.from(s.matchAll(/https?:\/\/[^\s)\]<>"']+/g)).map((m) =>
        m[0].replace(/[).,;:\]]+$/, '')
      )
    );
  const originalUrls = urlSet(original);
  if (originalUrls.size === 0) return { useOriginal: false };
  const formattedUrls = urlSet(formatted);
  let kept = 0;
  for (const u of originalUrls) if (formattedUrls.has(u)) kept += 1;
  // The formatting prompt mandates "Preserve ALL citations ... Keep hyperlinks
  // intact"; losing more than 30% of source URLs means the pass is unsafe.
  if (kept / originalUrls.size < 0.7) {
    return {
      useOriginal: true,
      reason: `formatted output retained only ${kept}/${originalUrls.size} source URLs`,
    };
  }
  return { useOriginal: false };
}

// ============================================================================
// Document Output Formatting
// ============================================================================

/**
 * Detect if content follows the document transcription/translation structure.
 * Supports both old format (with markdown ###, **) and new clean format.
 * Old: "### 1. DOCUMENT DESCRIPTION" and "**FILE:**"
 * New: "1. DOCUMENT DESCRIPTION" and "FILE:"
 */
export function isDocumentContent(text: string): boolean {
  if (!text) return false;
  // Check for document structure markers - support both old and new formats
  // Old format: ### 1. DOCUMENT DESCRIPTION  |  New format: 1. DOCUMENT DESCRIPTION
  const hasDescriptionSection = /(?:###?\s*)?1\.\s*DOCUMENT DESCRIPTION/i.test(text);
  // Old format: ### 2. FULL VERBATIM TRANSCRIPTION  |  New format: 2. FULL VERBATIM TRANSCRIPTION
  const hasTranscriptionSection = /(?:###?\s*)?2\.\s*FULL\s*(?:VERBATIM\s*)?TRANSCRIPTION/i.test(text);
  // Old format: **FILE:**  |  New format: FILE:
  const hasFileMarker = /(?:\*\*)?FILE:?(?:\*\*)?\s/i.test(text);
  const hasTypeMarker = /(?:\*\*)?TYPE:?(?:\*\*)?\s/i.test(text);
  // Must have at least description section AND one metadata marker
  return (hasDescriptionSection || hasTranscriptionSection) && (hasFileMarker || hasTypeMarker);
}

/**
 * Format document transcription/translation output to professional HTML.
 * Uses the same styling patterns as deep research and video script reports.
 *
 * Detects document structure patterns and renders with:
 * - Styled metadata card (FILE, TYPE, PAGES, LANGUAGE, LAYOUT & DESIGN, CONTENT SUMMARY)
 * - Clear visual separation between sections
 * - Professional typography for official documents
 */
export function formatDocumentToHtml(text: string): string {
  if (!text) return '';

  // If not document content, fall back to research formatter
  if (!isDocumentContent(text)) {
    return formatResearchToHtml(text);
  }

  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Parse the document structure
  const lines = text.split('\n');
  let currentSection: 'none' | 'description' | 'transcription' = 'none';
  const metadata: Record<string, string> = {};
  let layoutDesign = '';
  let contentSummary = '';
  let transcriptionContent: string[] = [];
  let descriptionContent: string[] = [];
  let detectedLanguage = '';

  // Parsing state
  let inLayoutDesign = false;
  let inContentSummary = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Detect section headers - support both old (###) and new (plain) formats
    if (/(?:###?\s*)?1\.\s*DOCUMENT DESCRIPTION/i.test(trimmed)) {
      currentSection = 'description';
      continue;
    }
    if (/(?:###?\s*)?2\.\s*FULL\s*(?:VERBATIM\s*)?TRANSCRIPTION/i.test(trimmed)) {
      currentSection = 'transcription';
      inLayoutDesign = false;
      inContentSummary = false;
      continue;
    }

    // Skip separator lines
    if (trimmed === '---') continue;

    // Parse metadata in description section
    if (currentSection === 'description') {
      // Check for metadata fields - support both old (**FIELD:**) and new (FIELD:) formats
      const fileMatch = trimmed.match(/^(?:\*\*)?FILE:?(?:\*\*)?\s*(.+)/i);
      const typeMatch = trimmed.match(/^(?:\*\*)?TYPE:?(?:\*\*)?\s*(.+)/i);
      const pagesMatch = trimmed.match(/^(?:\*\*)?PAGES(?:\/SECTIONS)?:?(?:\*\*)?\s*(.+)/i);
      const languageMatch = trimmed.match(/^(?:\*\*)?LANGUAGE:?(?:\*\*)?\s*(.+)/i);

      if (fileMatch) {
        metadata['FILE'] = fileMatch[1].trim();
        inLayoutDesign = false;
        inContentSummary = false;
        continue;
      }
      if (typeMatch) {
        metadata['TYPE'] = typeMatch[1].trim();
        inLayoutDesign = false;
        inContentSummary = false;
        continue;
      }
      if (pagesMatch) {
        metadata['PAGES'] = pagesMatch[1].trim();
        inLayoutDesign = false;
        inContentSummary = false;
        continue;
      }
      if (languageMatch) {
        metadata['LANGUAGE'] = languageMatch[1].trim();
        detectedLanguage = languageMatch[1].trim();
        inLayoutDesign = false;
        inContentSummary = false;
        continue;
      }

      // Check for layout & design section - support both old (**...**) and new (plain) formats
      if (/^(?:\*\*)?LAYOUT\s*[&|AND]*\s*DESIGN:?(?:\*\*)?/i.test(trimmed)) {
        inLayoutDesign = true;
        inContentSummary = false;
        // Extract inline content if any
        const inlineContent = trimmed.replace(/^(?:\*\*)?LAYOUT\s*[&|AND]*\s*DESIGN:?(?:\*\*)?\s*/i, '').trim();
        if (inlineContent) layoutDesign = inlineContent;
        continue;
      }

      // Check for content summary section - support both old (**...**) and new (plain) formats
      if (/^(?:\*\*)?CONTENT\s*SUMMARY:?(?:\*\*)?/i.test(trimmed)) {
        inContentSummary = true;
        inLayoutDesign = false;
        // Extract inline content if any
        const inlineContent = trimmed.replace(/^(?:\*\*)?CONTENT\s*SUMMARY:?(?:\*\*)?\s*/i, '').trim();
        if (inlineContent) contentSummary = inlineContent;
        continue;
      }

      // Accumulate content for current subsection
      if (inLayoutDesign && trimmed) {
        layoutDesign += (layoutDesign ? ' ' : '') + trimmed;
      } else if (inContentSummary && trimmed) {
        contentSummary += (contentSummary ? ' ' : '') + trimmed;
      } else if (!inLayoutDesign && !inContentSummary && trimmed) {
        descriptionContent.push(trimmed);
      }
    }

    // Accumulate transcription content
    if (currentSection === 'transcription' && trimmed) {
      transcriptionContent.push(line); // Keep original formatting
    }
  }

  // Build HTML output
  const blocks: string[] = [];

  // Section 1: Document Description & Summary Header (no emoji, no language badge)
  blocks.push(`
    <h2 style="margin:0 0 12px 0;font-size:16px;font-weight:800;color:#0b132b;letter-spacing:0.15px;border-bottom:1px solid #e2e8f0;padding-bottom:8px;">
      Document Description & Summary
    </h2>
  `);

  // Metadata card (use sentence case labels)
  const metadataRows: string[] = [];
  const metadataFields = [
    { key: 'FILE', label: 'File' },
    { key: 'TYPE', label: 'Type' },
    { key: 'PAGES', label: 'Pages' },
    { key: 'LANGUAGE', label: 'Language' },
  ];

  for (const field of metadataFields) {
    if (metadata[field.key]) {
      metadataRows.push(`
        <span style="font-weight:600;color:#64748b;font-size:0.8rem;">${field.label}</span>
        <span style="color:#1e293b;">${escape(metadata[field.key])}</span>
      `);
    }
  }

  if (metadataRows.length > 0) {
    blocks.push(`
      <div style="flex-shrink:0;padding:10px 12px;background:#f1f5f9;border-radius:6px;font-size:0.9rem;color:#334155;line-height:1.5;margin-bottom:12px;">
        <div style="display:grid;grid-template-columns:100px 1fr;gap:6px 12px;">
          ${metadataRows.join('')}
        </div>
      </div>
    `);
  }

  // Layout & Design section (sentence case, no uppercase transform)
  if (layoutDesign) {
    blocks.push(`
      <div style="flex-shrink:0;padding:10px 12px;background:#f8fafc;border-left:3px solid #94a3b8;border-radius:0 6px 6px 0;font-size:0.88rem;color:#475569;line-height:1.5;margin-bottom:12px;">
        <strong style="color:#64748b;font-size:0.8rem;display:block;margin-bottom:4px;">Layout & Design</strong>
        <p style="margin:0;">${escape(layoutDesign)}</p>
      </div>
    `);
  }

  // Content Summary box (highlighted, sentence case)
  if (contentSummary) {
    blocks.push(`
      <div style="flex-shrink:0;padding:10px 12px;background:#ede9fe;border-left:3px solid #7c3aed;border-radius:0 6px 6px 0;font-size:0.9rem;color:#334155;line-height:1.5;margin-bottom:12px;">
        <strong style="color:#7c3aed;font-size:0.8rem;display:block;margin-bottom:4px;">Content Summary</strong>
        <p style="margin:0;">${escape(contentSummary)}</p>
      </div>
    `);
  }

  // Visual separator
  blocks.push(`
    <div style="height:1px;background:linear-gradient(90deg, transparent 0%, #e2e8f0 20%, #e2e8f0 80%, transparent 100%);margin:16px 0;"></div>
  `);

  // Section 2: Full Transcription Header (no emoji, no language badge)
  blocks.push(`
    <h2 style="margin:0 0 12px 0;font-size:16px;font-weight:800;color:#0b132b;letter-spacing:0.15px;border-bottom:1px solid #e2e8f0;padding-bottom:8px;">
      Full Verbatim Transcription
    </h2>
  `);

  // Transcription content with table rendering
  if (transcriptionContent.length > 0) {
    // Helper to render a markdown table (lines starting with |) as HTML table
    const renderMarkdownTable = (tableLines: string[]): string => {
      if (tableLines.length === 0) return '';

      // Parse rows - each line is a row, cells are separated by |
      const parseRow = (line: string): string[] => {
        return line
          .split('|')
          .map(cell => cell.trim())
          .filter((cell, idx, arr) => idx > 0 && idx < arr.length - 1 || cell); // Remove empty first/last from | at edges
      };

      // Check if second line is a separator (contains only -, :, |, and spaces)
      const hasSeparator = tableLines.length > 1 && /^[\s|:-]+$/.test(tableLines[1]);
      const headerRow = parseRow(tableLines[0]);
      const bodyRows = (hasSeparator ? tableLines.slice(2) : tableLines.slice(1))
        .map(parseRow)
        .filter(row => row.length > 0);

      // Build HTML table
      let html = '<table style="border-collapse:collapse;width:100%;margin:12px 0;font-size:0.88rem;">';

      // Header row
      if (headerRow.length > 0) {
        html += '<thead><tr>';
        for (const cell of headerRow) {
          html += `<th style="border:1px solid #e2e8f0;padding:8px 12px;background:#f8fafc;font-weight:600;text-align:left;">${escape(cell)}</th>`;
        }
        html += '</tr></thead>';
      }

      // Body rows
      if (bodyRows.length > 0) {
        html += '<tbody>';
        for (const row of bodyRows) {
          html += '<tr>';
          for (const cell of row) {
            html += `<td style="border:1px solid #e2e8f0;padding:8px 12px;">${escape(cell)}</td>`;
          }
          html += '</tr>';
        }
        html += '</tbody>';
      }

      html += '</table>';
      return html;
    };

    // Process lines, grouping consecutive table lines together
    const outputParts: string[] = [];
    let tableBuffer: string[] = [];

    const flushTable = () => {
      if (tableBuffer.length > 0) {
        outputParts.push(renderMarkdownTable(tableBuffer));
        tableBuffer = [];
      }
    };

    for (const line of transcriptionContent) {
      const trimmed = line.trim();

      // Check if line is a table row (starts with |)
      if (trimmed.startsWith('|')) {
        tableBuffer.push(trimmed);
        continue;
      }

      // Not a table line - flush any pending table
      flushTable();

      if (!trimmed) {
        outputParts.push('<br/>');
        continue;
      }

      // Preserve page breaks
      if (/^---\s*Page\s*\d+/i.test(trimmed)) {
        outputParts.push(`<div style="text-align:center;color:#64748b;font-size:0.8rem;margin:12px 0;border-top:1px dashed #cbd5e1;padding-top:8px;">${escape(trimmed)}</div>`);
        continue;
      }

      // Regular paragraph
      outputParts.push(`<p style="margin:0 0 6px 0;white-space:pre-wrap;">${escape(trimmed)}</p>`);
    }

    // Flush any remaining table
    flushTable();

    blocks.push(`
      <div style="padding:14px 16px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;font-size:0.92rem;color:#1e293b;line-height:1.6;">
        ${outputParts.join('\n')}
      </div>
    `);
  }

  // Wrap in article container (same as formatResearchToHtml)
  return `<article style="font-size:15px;line-height:1.58;display:flex;flex-direction:column;gap:6px;color:#1e293b;font-family:'Inter','SF Pro Text',-apple-system,BlinkMacSystemFont,sans-serif;max-width:880px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:18px 22px;box-shadow:0 1px 4px rgba(0,0,0,0.04);">${blocks.join('\n')}</article>`;
}

// ============================================================================
// Error Formatting Helpers
// ============================================================================

/**
 * Format Perplexity API errors for user-friendly display.
 * Uses code names instead of provider names (Echo instead of Perplexity).
 * Uses model code names instead of raw model IDs.
 */
export function formatPerplexityError(err: unknown): string {
  const e = err as Record<string, unknown> | null;
  if (!e) return 'unknown error';
  const status = e.status ? `status ${e.status}` : '';
  const code = e.code ? `code ${e.code}` : '';
  // Use the versionless public station name instead of raw model ID
  const model = e.model ? `model ${getPublicSystemName(e.model as string)}` : '';
  const detail = typeof e.detail === 'string' ? sanitizeProviderNames(e.detail) : '';
  // Sanitize provider names from the error message
  const rawMessage = typeof e.message === 'string' ? e.message : 'request failed';
  const message = sanitizeProviderNames(rawMessage);
  const parts = [status, code, model, detail].filter(Boolean).join(', ');
  return parts ? `${message} (${parts})` : message;
}

// ============================================================================
// Video Script Helpers
// ============================================================================

/**
 * Strip markdown bold markers from text.
 * Used when text will be styled via HTML/CSS rather than markdown.
 */
function stripMarkdownBold(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '$1');
}

/**
 * Parse Stage 1 video script analysis response.
 * Handles two response formats:
 * 1. "PROCEED TO STAGE 2" with summary
 * 2. "CLARIFYING QUESTIONS NEEDED" with questions
 */
export function parseVideoScriptStage1Response(rawResponse: string): VideoScriptStage1Result {
  const cleaned = stripThinkingBlocks(rawResponse).trim();

  // Check if we should proceed directly (no clarifications needed)
  const proceedMatch = cleaned.match(/PROCEED TO STAGE 2/i);

  if (proceedMatch) {
    // Extract summary from the "Summary of understanding:" section
    const summary = extractVideoScriptSummary(cleaned);
    return {
      needsClarification: false,
      summary,
      clarifyingQuestions: [],
      rawResponse: cleaned,
    };
  }

  // Check if clarifications are needed
  const clarifyMatch = cleaned.match(/CLARIFYING QUESTIONS NEEDED/i);

  if (clarifyMatch) {
    // Extract the summary from "I understand you want a video about:" section
    const summaryMatch = cleaned.match(/I understand you want a video about:\s*([^\n]+)/i);
    const topicSummary = summaryMatch ? stripMarkdownBold(summaryMatch[1].trim()) : '';

    // Extract questions
    const questions = extractVideoScriptClarifyingQuestions(cleaned);

    // Only require clarification if we actually found questions
    // If extraction failed (questions array is empty), fall back to "proceed" mode
    // This handles cases where Claude says "CLARIFYING QUESTIONS NEEDED" but formats
    // the questions differently than expected (e.g., from chain workflow content)
    if (questions.length === 0) {
      console.warn('[VideoScript] CLARIFYING QUESTIONS NEEDED found but no questions extracted - proceeding to Stage 2');
      const summary = extractVideoScriptSummary(cleaned);
      return {
        needsClarification: false,
        summary: summary.topic ? summary : {
          topic: topicSummary || 'Video Script Topic',
          targetAudience: '',
          emotionalState: '',
          platformAndLength: '',
          primaryGoalCta: '',
        },
        clarifyingQuestions: [],
        rawResponse: cleaned,
      };
    }

    return {
      needsClarification: true,
      summary: {
        topic: topicSummary,
        targetAudience: '',
        emotionalState: '',
        platformAndLength: '',
        primaryGoalCta: '',
      },
      clarifyingQuestions: questions,
      rawResponse: cleaned,
    };
  }

  // Fallback: If response doesn't match expected format, assume clarification needed
  // and try to extract any questions
  const questions = extractVideoScriptClarifyingQuestions(cleaned);

  return {
    needsClarification: questions.length > 0,
    summary: {
      topic: '',
      targetAudience: '',
      emotionalState: '',
      platformAndLength: '',
      primaryGoalCta: '',
    },
    clarifyingQuestions: questions,
    rawResponse: cleaned,
  };
}

/**
 * Extract summary fields from "PROCEED TO STAGE 2" response.
 */
function extractVideoScriptSummary(text: string): VideoScriptSummary {
  const extractField = (pattern: RegExp): string => {
    const match = text.match(pattern);
    return match ? match[1].trim() : '';
  };

  return {
    topic: extractField(/[-•]\s*Topic:\s*(.+?)(?:\n|$)/i),
    targetAudience: extractField(/[-•]\s*Target audience:\s*(.+?)(?:\n|$)/i),
    emotionalState: extractField(/[-•]\s*Emotional state:\s*(.+?)(?:\n|$)/i),
    platformAndLength: extractField(/[-•]\s*Platform\s*\+?\s*length:\s*(.+?)(?:\n|$)/i),
    primaryGoalCta: extractField(/[-•]\s*Primary goal\/CTA:\s*(.+?)(?:\n|$)/i),
  };
}

/**
 * Extract clarifying questions from the response.
 */
function extractVideoScriptClarifyingQuestions(text: string): VideoScriptClarifyingQuestion[] {
  const questions: VideoScriptClarifyingQuestion[] = [];

  // Match numbered questions (1., 2., etc.) - supports both formats
  // Format 1: "1. [Question]" on one line, then "- Why this matters:" and "- Example options:"
  // Format 2: Question followed by indented details
  const questionPattern = /(\d+)\.\s*([^\n]+?)(?:\n|$)/g;
  let match;

  while ((match = questionPattern.exec(text)) !== null) {
    const questionNum = parseInt(match[1], 10);
    const questionText = stripMarkdownBold(match[2].trim());

    // Skip if this looks like a list item within another section
    if (questionNum > 4 || !questionText) continue;

    // Find the section after this question (until the next numbered question or end)
    const startPos = match.index + match[0].length;
    const nextQuestionMatch = text.slice(startPos).match(/\n\d+\.\s+/);
    const endPos = nextQuestionMatch
      ? startPos + (nextQuestionMatch.index || text.length)
      : text.length;
    const questionSection = text.slice(startPos, endPos);

    // Extract "Why this matters" - handle both formats
    const whyMatch = questionSection.match(/[-•]\s*Why this matters:\s*(.+?)(?:\n|$)/i);
    const whyItMatters = whyMatch ? stripMarkdownBold(whyMatch[1].trim()) : '';

    // Extract "Example options" - parse the slash-separated or comma-separated options
    const optionsMatch = questionSection.match(/[-•]\s*Example options:\s*(.+?)(?:\n|$)/i);
    let options: string[] = [];
    if (optionsMatch) {
      const optionsText = optionsMatch[1].trim();
      // Split by " / " or ", " or "|"
      options = optionsText
        .split(/\s*[\/|]\s*|\s*,\s*/)
        .map(o => o.trim().replace(/^\[|\]$/g, '').trim())
        .filter(o => o.length > 0);
    }

    questions.push({
      question: questionText,
      whyItMatters,
      options,
    });
  }

  return questions;
}

/**
 * Format clarifying questions as HTML for display in chat.
 * Uses generateSpecialMessageTemplate with green theme.
 * Displays questions as a simple list - user responds with text input or clicks button.
 */
export function formatVideoScriptClarifyingQuestionsHtml(
  summary: string,
  questions: VideoScriptClarifyingQuestion[],
  proceedButtonId: string
): string {
  const { accent, muted } = VIDEO_SCRIPT_COLORS;

  if (questions.length === 0) {
    return generateSpecialMessageTemplate({
      header: { title: 'no questions generated', icon: '!' },
      sections: [
        { type: 'text', content: 'No clarifying questions were generated. Please try again.', style: { fontSize: '0.9rem' } },
      ],
      theme: 'custom',
      customColors: {
        accent: '#dc2626',
        accentBg: '#fef2f2',
        border: '#fca5a5',
        subtitle: '#7f1d1d',
        muted: '#991b1b',
      },
    });
  }

  // Build questions list with "why this matters" subtext
  // Use tighter spacing for compact, scannable layout - NO whitespace in template literals
  const questionsList = questions.map((q, index) => {
    const isLast = index === questions.length - 1;
    const whyMatters = q.whyItMatters
      ? `<div style="font-size:0.78rem;color:${muted};font-style:italic;margin-top:1px;line-height:1.35;">${q.whyItMatters}</div>`
      : '';

    // IMPORTANT: No whitespace between <li> and content to prevent spacing issues
    return `<li style="margin-bottom:${isLast ? '0' : '8px'};color:#334155;font-size:0.86rem;line-height:1.4;"><span style="font-weight:600;">${q.question}</span>${whyMatters}</li>`;
  }).join('');

  const questionsHtml = `<ul style="margin:0;padding:0 0 0 18px;list-style-type:disc;list-style-position:outside;">${questionsList}</ul>`;

  return generateSpecialMessageTemplate({
    header: { title: 'a few questions to optimize your script', icon: '?' },
    sections: [
      // NO PREVIEW WINDOWS IN ACTION TILES (owner directive 2026-08-29):
      // this echo of the user's own request used to sit in an inset slate
      // box inside an already-bordered tile — double chrome, off-palette.
      // It is a plain line now.
      ...(summary ? [{
        type: 'custom' as const,
        content: `<div style="font-size:0.86rem;line-height:1.5;color:#6B6459;">I understand you want a video about: <strong style="color:#1C1917;">${summary}</strong></div>`,
      }] : []),
      // Questions as custom HTML list
      { type: 'custom' as const, content: questionsHtml },
    ],
    buttons: [
      { label: 'write video script', id: proceedButtonId, background: accent },
    ],
    helperText: `<strong style="color:${accent};">Or reply below</strong> with any additional context, or type <strong>'skip'</strong> to proceed, or <strong>'cancel'</strong> to exit.`,
    theme: 'green',
    sectionGap: '10px',
  });
}

/**
 * Format deep research qualification/Stage 1 message as HTML.
 * Uses generateSpecialMessageTemplate with blue theme to match standard formatting.
 *
 * @param initialFindings - Preliminary findings text (markdown)
 * @param howAnswersHelp - Dynamic "how answers help" from API, or null for static fallback
 * @param clarifyingQuestions - Array of questions to ask
 * @param skipButtonId - Button ID for skip action
 * @param videoScriptButtonId - Button ID for video script action (ignored if secondaryCtaOverride.hidden is true)
 * @param workflowOutputId - Optional workflow output ID for context passing
 * @param language - Output language ('en' or 'es', default 'en')
 * @param secondaryCtaOverride - Optional override for the secondary CTA.
 *   - `{ hidden: true }` omits the secondary button entirely (legacy
 *     suppression shape kept for back-compat; the task-queue path
 *     post-2026-05-20 instead passes a label + id + onClickAction below).
 *   - `{ label: '...' }` swaps just the visible label (e.g. localised
 *     "Answer questions" instead of "write video script") while keeping
 *     the default `videoScriptButtonId` id.
 *   - `{ label, id, onClickAction }` fully overrides the secondary CTA:
 *     a custom button id (so a dedicated handler in GlobalChatSystem.tsx
 *     can route the click), and `onClickAction` is surfaced as a
 *     `data-cta-action` attribute on the rendered <button> for any
 *     downstream dispatcher that prefers attribute-routed click handling
 *     over id-routed (the existing id-routed pattern keeps working).
 *   - Default (undefined) preserves legacy behavior: renders the
 *     "write video script" secondary button alongside the primary skip
 *     CTA, used by the Maestro / global-tools surface in
 *     src/services/globalTools/tools/deepResearch.ts.
 *
 * BUG-DR-CLARIFYING-WRONG-CTA (2026-05-20) — the task-queue clarifying
 * card path shipped in PR #194 reused this helper and inherited the
 * Maestro-only "write video script" secondary CTA, which made no sense
 * on that path. The override parameter lets the task-queue caller drop
 * the secondary button without forking the renderer.
 *
 * BUG-DR-CARD-FINAL-CLEANUP (2026-05-20, PR #196 follow-up to PR #195) —
 * Malaika "bring both buttons and remove questionary paragraph". The
 * task-queue path now re-renders TWO buttons: green primary skip-to-DR
 * + grey secondary "Answer questions" (en) / "Responder preguntas" (es)
 * which simply .focus()es the chat composer below the card. The
 * `suppressHelperText` flag drops the prose footer beneath the buttons
 * for this surface (the two clearly labeled buttons are self-explanatory).
 * DO NOT change the default branch — the Maestro surface legitimately
 * offers the video script handoff and the helperText footer.
 *
 * @param suppressHelperText - If true, the helperText footer
 *   (`"Reply below with your answers, or type 'skip' to proceed..."`)
 *   is omitted from the rendered card. Used by the task-queue clarifying
 *   path where the two visible buttons obviate the prose explainer.
 *   Default (undefined / false) preserves the legacy footer for the
 *   Maestro / global-tools surface.
 */
export function formatDeepResearchQualificationHtml(
  initialFindings: string,
  howAnswersHelp: string | null,
  clarifyingQuestions: string[],
  skipButtonId: string,
  videoScriptButtonId: string,
  workflowOutputId?: string,
  language: 'en' | 'es' = 'en',
  secondaryCtaOverride?: {
    hidden?: boolean;
    label?: string;
    id?: string;
    onClickAction?: 'focus-composer' | 'send-skip' | string;
  },
  suppressHelperText?: boolean,
  // BUG-DR-SKIP-ORIGINAL-QUERY (2026-06-05) — when provided, stamped onto the
  // skip button as `data-original-query` so the skip-click handler can recover
  // the original DR prompt WITHOUT walking the active thread's in-memory
  // history (which breaks under thread fragmentation). Optional / backward
  // compatible (trailing param).
  originalQuery?: string,
  // PLAN-deep-research-dynamic-clarify-20260713 — dynamic multi-round loop
  // progress. When provided AND round >= 2, a localized "round N of M"
  // suffix is appended to the card header so follow-up rounds are visibly
  // part of one bounded loop. Round-1 cards render byte-identically to the
  // pre-loop shape (the header phrase is a detection contract for the
  // refinement guard's text-shape matchers — suffix-only, never replaced).
  roundInfo?: { round: number; maxRounds: number },
  // ANSWER-FIRST (owner directive 2026-08-20): Stage-1 now ANSWERS the
  // request in initialFindings and separately judges whether the full
  // report is worth offering. `false` (with no questions) renders a pure
  // answer card — no offer section, no run button, no helper line.
  // Undefined/true keeps the offer (legacy payloads, legal track).
  offerDeepResearch?: boolean,
): { mainHtml: string; buttonsHtml: string } {
  const { accent, muted } = SPECIAL_MESSAGE_THEMES.blue;
  // ANSWER-FIRST REDESIGN (owner directive 2026-08-20, supersedes the
  // 2026-08-19 findings-first pass): the card leads with the ANSWER
  // (initialFindings is now the summarized answer to the user's request),
  // then strategically OFFERS the fully-cited deep research report. The
  // "how your answers will help" benefits block is REMOVED entirely, and
  // the "answer any of the questions below…" helper line renders ONLY
  // when the card actually carries questions. The old header phrase stays
  // recognized forever by the refinement guard / history matchers.
  const chrome = language === 'es'
    ? {
        researchReadyTitle: 'investigación lista',
        researchReadyBody: 'Listo para comenzar la investigación. Haz clic abajo para continuar.',
        startResearchButton: 'comenzar investigación',
        headerTitle: 'hallazgos iniciales',
        offerTitle: '¿quieres el informe completo de investigación a fondo?',
        offerBody: 'totalmente citado y con fuentes verificadas, entregado como PDF + DOCX — tus respuestas rápidas abajo lo hacen más preciso y estratégico.',
        offerBodyNoQuestions: 'totalmente citado y con fuentes verificadas, entregado como PDF + DOCX.',
        helperLine: 'responde cualquiera de las preguntas abajo en el chat — o genera el informe ahora.',
        skipButton: 'ir directo a la investigación',
        runReportButton: 'generar el informe completo',
        videoScriptButton: 'escribir guión de video',
      }
    : {
        researchReadyTitle: 'research ready',
        researchReadyBody: 'Ready to begin research. Click below to proceed.',
        startResearchButton: 'start research',
        headerTitle: 'initial findings',
        offerTitle: 'want the full deep research report?',
        offerBody: 'fully cited and source-verified, delivered as PDF + DOCX — your quick answers below make it sharper and more strategic.',
        offerBodyNoQuestions: 'fully cited and source-verified, delivered as PDF + DOCX.',
        helperLine: 'answer any of the questions below in chat — or run the report now.',
        skipButton: 'skip to deep research',
        runReportButton: 'run the full report',
        videoScriptButton: 'write video script',
      };

  // Build data attributes for buttons (if workflow output ID provided)
  // BUG-DR-SKIP-ORIGINAL-QUERY (2026-06-05) — also stamp `original-query` when
  // provided so the skip button carries its own query (thread-fragmentation
  // proof). Rendered as `data-original-query="<escaped>"` by
  // generateWorkflowActionButtonsHtml (escapeHtmlAttribute handles quoting).
  const trimmedOriginalQuery = typeof originalQuery === 'string' ? originalQuery.trim() : '';
  const buttonDataAttrs: Record<string, string> | undefined =
    workflowOutputId || trimmedOriginalQuery
      ? {
          ...(workflowOutputId ? { 'workflow-output-id': workflowOutputId } : {}),
          ...(trimmedOriginalQuery ? { 'original-query': trimmedOriginalQuery } : {}),
        }
      : undefined;

  // Edge case: no findings and no questions
  if (!initialFindings?.trim() && clarifyingQuestions.length === 0) {
    const mainHtml = generateSpecialMessageTemplate({
      header: { title: chrome.researchReadyTitle, icon: '?' },
      sections: [{ type: 'text', content: chrome.researchReadyBody }],
      theme: 'blue',
    });

    const buttonsHtml = generateWorkflowActionButtonsHtml([
      { label: chrome.startResearchButton, id: skipButtonId, background: accent, dataAttributes: buttonDataAttrs },
    ], { theme: 'blue' });

    return { mainHtml, buttonsHtml };
  }

  // Convert findings markdown to HTML
  const findingsHtml = convertMarkdownToHtml(initialFindings || '');

  // ANSWER-FIRST (2026-08-20): the "how your answers will help" benefits
  // block is removed entirely — the parameter is kept for call-site
  // compatibility but deliberately unused.
  void howAnswersHelp;

  // Strategic offer visibility: the offer + run button disappear ONLY when
  // the model explicitly declined the offer AND asked nothing — a model
  // that still asks questions is sharpening the report, so the offer
  // stays regardless of an inconsistent offerDeepResearch=false.
  const cardHasQuestions = clarifyingQuestions.length > 0;
  const showReportOffer = offerDeepResearch !== false || cardHasQuestions;

  // Build questions list (matching video script pattern - no whitespace)
  const questionsList = clarifyingQuestions.map((q, index) => {
    const isLast = index === clarifyingQuestions.length - 1;
    return `<li style="margin-bottom:${isLast ? '0' : '8px'};color:#1C1917;font-size:0.86rem;line-height:1.4;"><span style="font-weight:600;">${q}</span></li>`;
  }).join('');
  const questionsListHtml = `<ul style="margin:0;padding:0 0 0 18px;list-style-type:disc;list-style-position:outside;">${questionsList}</ul>`;

  // Build sections array
  const sections: TemplateSectionConfig[] = [];

  // 1. Initial findings box (untitled — the card HEADER is "initial
  //    findings" now, so a box title would duplicate it)
  if (findingsHtml.trim()) {
    // NO PREVIEW WINDOWS IN ACTION TILES (owner directive 2026-08-29): the
    // findings ARE this card's body — they were never a preview of content
    // shown elsewhere — so they read as the tile's own prose instead of an
    // inset tinted pane nested in an already-bordered tile.
    sections.push({
      type: 'custom' as const,
      content: `<div style="font-size:0.9rem;line-height:1.55;color:#1C1917;">${findingsHtml}</div>`,
    });
  }

  // 2. The report offer — the answer first, then the strategic ask (owner
  //    directive 2026-08-20): offered only when the model judged the full
  //    report genuinely valuable (or is still asking sharpening questions).
  //    The body's "your quick answers below…" clause only renders when
  //    questions actually exist below it.
  if (showReportOffer) {
    const offerBody = cardHasQuestions ? chrome.offerBody : chrome.offerBodyNoQuestions;
    sections.push({
      type: 'custom' as const,
      content: `<div style="font-size:0.9rem;margin-bottom:2px;"><strong style="color:${accent};">${chrome.offerTitle}</strong></div><div style="font-size:0.85rem;line-height:1.5;color:${muted};">${offerBody}</div>`,
    });
  }

  // 3. Clarifying questions (if present)
  if (cardHasQuestions) {
    sections.push({ type: 'custom' as const, content: questionsListHtml });
  }

  // Generate main HTML WITHOUT buttons.
  //
  // BUG-DR-CARD-FINAL-CLEANUP (2026-05-20) — the task-queue caller passes
  // suppressHelperText=true to omit the "Reply below with your answers,
  // or type 'skip' to proceed..." footer beneath the buttons. With the
  // two clearly labeled buttons ("skip to deep research" + "Answer
  // questions" / "Responder preguntas") the prose explainer is
  // redundant clutter. The Maestro / global-tools call site does NOT
  // pass this flag and continues to render the footer (its surface
  // historically depended on the prose to point users at the composer).
  const roundSuffix =
    roundInfo && Number.isFinite(roundInfo.round) && roundInfo.round >= 2 && Number.isFinite(roundInfo.maxRounds)
      ? (language === 'es'
          ? ` — ronda ${roundInfo.round} de ${roundInfo.maxRounds}`
          : ` — round ${roundInfo.round} of ${roundInfo.maxRounds}`)
      : '';

  // ANSWER-FIRST (2026-08-20): the helper line is the "answer below in
  // chat" affordance for the QUESTIONS — a card with no questions must not
  // tell the user to "answer any of the questions below" (owner screenshot
  // 2026-08-19: zero-question card carried the line). The legacy
  // suppressHelperText flag stays inert (kept for call-site compatibility).
  void suppressHelperText;
  const mainHtml = generateSpecialMessageTemplate({
    header: { title: `${chrome.headerTitle}${roundSuffix}`, icon: '?' },
    sections,
    ...(cardHasQuestions
      ? { helperText: `<strong style="color:${accent};">${chrome.helperLine}</strong>` }
      : {}),
    theme: 'blue',
    sectionGap: '10px',
    // NO buttons parameter - buttons will be generated separately
  });

  // Generate buttons HTML separately
  //
  // UX 2026-05-14: `skipButton` ("ir directo a la investigación" / "skip to deep
  // research") is the primary CTA -- it's the action most users want from this
  // card. `videoScriptButton` is a secondary path. Previously both colors were
  // inverted (primary CTA rendered gray, secondary green), which led to demo-day
  // confusion (QA thread C0AM8U804KZ/1778481193.723179, Malaika "which one???").
  // Swap so the primary CTA is the colored emphasis button and the secondary is
  // the neutral one. Colors themselves are unchanged; only their assignment is.
  //
  // BUG-DR-CLARIFYING-WRONG-CTA (2026-05-20) — task-queue caller can pass
  // secondaryCtaOverride={ hidden: true } to omit the "write video script"
  // button entirely on the clarifying-card path. Default (undefined) keeps
  // the legacy 2-button shape for the Maestro / global-tools surface.
  // ANSWER-FIRST (2026-08-20): a declined offer means no run button — the
  // card is the answer. (Questions present ⇒ showReportOffer is true and
  // the run button stays, see the visibility rule above.)
  // ANSWER-FIRST (2026-08-20): with no questions there is nothing to "skip"
  // — the button says what it does. Same id either way (click handlers and
  // the CTA-lifecycle registry key off the id, not the label).
  const buttonDefs: Array<Parameters<typeof generateWorkflowActionButtonsHtml>[0][number]> = showReportOffer
    ? [
        {
          label: cardHasQuestions ? chrome.skipButton : chrome.runReportButton,
          id: skipButtonId,
          background: PREST0_PRIMARY_CTA_BG,
          dataAttributes: buttonDataAttrs,
        },
      ]
    : [];
  if (!secondaryCtaOverride?.hidden) {
    const secondaryLabel = (secondaryCtaOverride?.label && secondaryCtaOverride.label.trim())
      ? secondaryCtaOverride.label
      : chrome.videoScriptButton;
    // BUG-DR-CARD-FINAL-CLEANUP (2026-05-20) — when the caller supplies a
    // dedicated secondary `id` (e.g. 'task-queue-dr-clarifying-answer-btn'),
    // use it instead of `videoScriptButtonId` so the GlobalChatSystem.tsx
    // click dispatcher can route it via its own branch (focus the composer
    // for the task-queue path; legacy global-tools awaiting-input runtime
    // for the Maestro path). `onClickAction` rides along as a
    // `data-cta-action` attribute so any attribute-routed dispatcher in
    // the future can drive behavior without piggybacking on id strings.
    const secondaryId = (secondaryCtaOverride?.id && secondaryCtaOverride.id.trim())
      ? secondaryCtaOverride.id
      : videoScriptButtonId;
    const secondaryDataAttrs: Record<string, string> = { ...(buttonDataAttrs || {}) };
    if (secondaryCtaOverride?.onClickAction) {
      secondaryDataAttrs['cta-action'] = secondaryCtaOverride.onClickAction;
    }
    buttonDefs.push({
      label: secondaryLabel,
      id: secondaryId,
      background: PREST0_SECONDARY_CTA_BG,
      dataAttributes: Object.keys(secondaryDataAttrs).length ? secondaryDataAttrs : undefined,
    });
  }
  const buttonsHtml = buttonDefs.length > 0
    ? generateWorkflowActionButtonsHtml(buttonDefs, { theme: 'blue' })
    : '';

  return { mainHtml, buttonsHtml };
}

/**
 * Format the "proceed to Stage 2" summary as HTML.
 * Uses generateSpecialMessageTemplate with green theme.
 */
export function formatVideoScriptProceedHtml(summary: VideoScriptSummary): string {
  const { subtitle } = VIDEO_SCRIPT_COLORS;

  const summaryItems = [
    { label: 'Topic', value: summary.topic },
    { label: 'Target Audience', value: summary.targetAudience },
    { label: 'Emotional State', value: summary.emotionalState },
    { label: 'Platform + Length', value: summary.platformAndLength },
    { label: 'Primary Goal/CTA', value: summary.primaryGoalCta },
  ].filter(item => item.value);

  const summaryHtml = summaryItems.length > 0
    ? summaryItems.map(item =>
        `<div style="font-size:0.9rem;"><strong style="color:#334155;">${item.label}:</strong> <span style="color:${subtitle};">${item.value}</span></div>`
      ).join('')
    : '';

  return generateSpecialMessageTemplate({
    header: { title: 'ready to generate video script', icon: '✓' },
    sections: [
      { type: 'text', content: 'I have enough information to create your script. Generating now...', style: { fontSize: '0.9rem' } },
      // De-boxed 2026-08-29 (no preview windows in action tiles): the
      // collected answers read as the tile's own lines.
      ...(summaryHtml ? [{
        type: 'custom' as const,
        content: `<div style="font-size:0.86rem;line-height:1.55;color:#1C1917;">${summaryHtml}</div>`,
      }] : []),
    ],
    // House theme — CONFIRMATION IS PREST0 GOLD, NEVER GREEN (owner
    // directive 2026-08-25). 'green' rendered #059669/#10b981 here.
    theme: 'blue',
  });
}

// ============================================================================
// Video Script Stage 2 Helpers
// ============================================================================

/**
 * Parsed video script from Stage 2 response.
 */
export interface ParsedVideoScript {
  title: string;
  estimatedLength: string;
  script: string;
  productionNotes: {
    hookStrategy: string;
    recommendedTone: string;
    emphasisPoints: string[];
    suggestedPacing: string;
  };
  characterCount: number;
  wordCount: number;
}

/**
 * Post-generation polish pass for video scripts — catches recurring Spanish
 * typos and half-English phrasings that the LLM sometimes slips through the
 * Stage 2 prompt guardrails. Safe to run on any language; only operates on
 * patterns that are unambiguously wrong in Spanish and have no valid English
 * reading, so it's a no-op for all-English scripts.
 *
 * MT-QA G6 (2026-05-08 Video Script QA):
 *   - "esta information"              → "esta información"
 *   - "hacer hincapié en el enlace …"  → "hacer clic en el enlace …"
 *     (hacer hincapié = "emphasize", never used for a clickable link)
 *   - "hacer click en"                → "hacer clic en"
 *
 * Keep this list conservative — only add replacements where the English
 * reading would itself be a bug.
 */
export function polishVideoScriptSpanish(script: string): string {
  if (!script) return script;
  let out = script;

  // "esta information" / "esa information" → "esta/esa información"
  out = out.replace(/\b(esta|esa|la|una|esta\s+es\s+una)\s+information\b/gi, (_m, p1) => `${p1} información`);

  // Any bare "information" inside an otherwise Spanish sentence is very hard
  // to detect deterministically; only fix the common "toda la information"
  // and "la information" patterns where Spanish articles precede it.
  out = out.replace(/\b(toda\s+la|la|esa|esta|esta\s+información\s+general\s+y\s+no)\s+information\b/gi, (_m, p1) => `${p1} información`);

  // "hacer hincapié en el enlace abajo" (emphasize the link below) →
  // the model meant "click the link below" in every observed case.
  out = out.replace(
    /\bhacer\s+hincapi[eé]\s+en\s+el\s+enlace\s+(?:de\s+)?abajo\b/gi,
    'hacer clic en el enlace de abajo'
  );
  out = out.replace(
    /\bhacer\s+hincapi[eé]\s+en\s+el\s+enlace\b/gi,
    'hacer clic en el enlace'
  );

  // Half-English "hacer click" → correct Spanish "hacer clic"
  out = out.replace(/\bhacer\s+click\b/gi, 'hacer clic');
  out = out.replace(/\bhaga\s+click\b/gi, 'haga clic');
  out = out.replace(/\bhaz\s+click\b/gi, 'haz clic');

  return out;
}

/**
 * Parse Stage 2 video script generation response.
 * Extracts title, script content, and production notes.
 */
export function parseVideoScriptStage2Response(rawResponse: string): ParsedVideoScript {
  const cleaned = stripThinkingBlocks(rawResponse).trim();

  // Extract title
  const titleMatch = cleaned.match(/TITLE:\s*(.+?)(?:\n|$)/i)
    || cleaned.match(/^#\s*(.+?)(?:\n|$)/m)
    || cleaned.match(/^##\s*(.+?)(?:\n|$)/m);
  const title = titleMatch ? titleMatch[1].trim() : 'Untitled Video Script';

  // Extract estimated length
  const lengthMatch = cleaned.match(/ESTIMATED LENGTH:\s*(.+?)(?:\n|$)/i)
    || cleaned.match(/(?:\*\*)?Duration(?:\*\*)?:\s*(.+?)(?:\n|$)/i)
    || cleaned.match(/(?:\*\*)?Estimated Length(?:\*\*)?:\s*(.+?)(?:\n|$)/i);
  const estimatedLength = lengthMatch ? lengthMatch[1].trim() : '2-3 minutes';

  // Extract script content (between markers)
  let script = '';
  const scriptStartMatch = cleaned.match(/---SCRIPT START---\s*([\s\S]*?)\s*---SCRIPT END---/i);
  if (scriptStartMatch) {
    script = scriptStartMatch[1].trim();
  } else {
    // Fallback 1: exact-format response missing end markers
    const exactFormatFallback = cleaned.match(/ESTIMATED LENGTH:[^\n]*\n([\s\S]*?)(?:PRODUCTION NOTES:|$)/i);
    if (exactFormatFallback) {
      script = exactFormatFallback[1].trim()
        .replace(/^---SCRIPT START---\s*/i, '')
        .replace(/\s*---SCRIPT END---$/i, '')
        .trim();
    }

    // Fallback 2: markdown-style output from the model
    // Example observed output:
    //   # Title
    //   ## PRODUCTION NOTES
    //   **Duration:** ~2 minutes
    //   ...
    //   ## FULL SCRIPT
    //   [script body]
    if (!script) {
      const markdownScriptMatch = cleaned.match(/##\s*(?:📋\s*)?FULL SCRIPT\s*([\s\S]*?)(?:##\s*(?:🎬\s*)?PRODUCTION NOTES|$)/i)
        || cleaned.match(/##\s*SCRIPT\s*([\s\S]*?)(?:##\s*PRODUCTION NOTES|$)/i);

      if (markdownScriptMatch) {
        script = markdownScriptMatch[1]
          .trim()
          .replace(/^---\s*/gm, '')
          .replace(/^\*\*\[(.+?)\]\*\*$/gm, '[$1]')
          .trim();
      }
    }

    // Fallback 3: if production notes come first, take everything after FULL SCRIPT marker
    if (!script) {
      const afterFullScript = cleaned.split(/##\s*(?:📋\s*)?FULL SCRIPT/i)[1];
      if (afterFullScript) {
        script = afterFullScript.trim();
      }
    }
  }

  // Extract production notes
  const productionNotesSection = cleaned.match(/PRODUCTION NOTES:\s*([\s\S]*?)$/i)
    || cleaned.match(/##\s*(?:🎬\s*)?PRODUCTION NOTES\s*([\s\S]*?)$/i);
  const notesText = productionNotesSection ? productionNotesSection[1] : '';

  const hookStrategyMatch = notesText.match(/[-•]\s*Hook strategy:\s*(.+?)(?:\n|$)/i);
  const toneMatch = notesText.match(/[-•]\s*Recommended tone:\s*(.+?)(?:\n|$)/i)
    || notesText.match(/(?:\*\*)?Tone(?:\*\*)?:\s*(.+?)(?:\n|$)/i);
  const pacingMatch = notesText.match(/[-•]\s*Suggested pacing:\s*(.+?)(?:\n|$)/i)
    || notesText.match(/(?:\*\*)?Pacing(?:\*\*)?:\s*(.+?)(?:\n|$)/i);

  // Extract emphasis points (may be on one line or multiple)
  let emphasisPoints: string[] = [];
  const emphasisMatch = notesText.match(/[-•]\s*Emphasis points?:\s*(.+?)(?:\n[-•]|$)/is)
    || notesText.match(/(?:\*\*)?On-screen text suggestions(?:\*\*)?:\s*(.+?)(?:\n|$)/i);
  if (emphasisMatch) {
    const emphasisText = emphasisMatch[1].trim();
    emphasisPoints = emphasisText
      .split(/[;,]|\n\s*[-•]|\n\s*\d+\.\s*/)
      .map(p => p.trim().replace(/^["']|["']$/g, '').replace(/^\d+\.\s*/, ''))
      .filter(p => p.length > 0);
  }

  // Final cleanup: if script still contains notes or headings, strip obvious wrappers
  script = script
    .replace(/^##\s*(?:📋\s*)?FULL SCRIPT\s*/i, '')
    .replace(/##\s*(?:🎬\s*)?PRODUCTION NOTES[\s\S]*$/i, '')
    .trim();

  // MT-QA-BUG (G6 Video Script QA 2026-05-08): apply a small deterministic
  // post-generation polish pass to catch recurring Spanish typos that the
  // LLM slips through even with explicit prompt guardrails.
  script = polishVideoScriptSpanish(script);

  return {
    title,
    estimatedLength,
    script,
    productionNotes: {
      hookStrategy: hookStrategyMatch ? hookStrategyMatch[1].trim() : '',
      recommendedTone: toneMatch ? toneMatch[1].trim() : '',
      emphasisPoints,
      suggestedPacing: pacingMatch ? pacingMatch[1].trim() : '',
    },
    characterCount: script.length,
    wordCount: script.split(/\s+/).filter(w => w.length > 0).length,
  };
}

/**
 * Format the generated video script as HTML for display in chat.
 * Uses generateSpecialMessageTemplate with green theme.
 * Includes metadata, script content, production notes, and action buttons.
 */
export function formatVideoScriptOutputHtml(
  parsedScript: ParsedVideoScript,
  translateButtonId: string
): string {
  const { title, estimatedLength, script, productionNotes, characterCount, wordCount } = parsedScript;
  const { subtitle, muted } = VIDEO_SCRIPT_COLORS;

  // Escape HTML in script content but preserve line breaks
  const escapeHtml = (text: string) =>
    text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  // Format script with paragraph breaks - tighter spacing for video scripts
  const formattedScript = escapeHtml(script)
    .split(/\n\n+/)
    .map(p => `<p style="margin:0 0 8px 0;line-height:1.55;">${p.replace(/\n/g, '<br/>')}</p>`)
    .join('');

  // Video icon SVG
  const videoIconSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>`;

  // Build metadata HTML - compact inline display
  const metadataHtml = `<div style="display:flex;flex-wrap:wrap;gap:8px;font-size:0.82rem;color:${muted};">
    <span>${escapeHtml(estimatedLength)}</span>
    <span style="opacity:0.5;">•</span>
    <span>${characterCount.toLocaleString()} characters</span>
    <span style="opacity:0.5;">•</span>
    <span>${wordCount.toLocaleString()} words</span>
  </div>`;

  // Build script content HTML - refined padding and typography
  const scriptHtml = `<div style="padding:12px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;font-size:0.92rem;color:#1e293b;line-height:1.6;">${formattedScript}</div>`;

  // Build production notes HTML - improved spacing and hierarchy
  const notesItems: string[] = [];
  if (productionNotes.hookStrategy) {
    notesItems.push(`<div style="line-height:1.45;"><span style="font-weight:600;color:#334155;">hook strategy:</span> ${escapeHtml(productionNotes.hookStrategy)}</div>`);
  }
  if (productionNotes.recommendedTone) {
    notesItems.push(`<div style="line-height:1.45;"><span style="font-weight:600;color:#334155;">recommended tone:</span> ${escapeHtml(productionNotes.recommendedTone)}</div>`);
  }
  if (productionNotes.emphasisPoints.length > 0) {
    const emphasisList = productionNotes.emphasisPoints
      .map((p, i) => `<li style="margin-bottom:${i === productionNotes.emphasisPoints.length - 1 ? '0' : '3px'};">"${escapeHtml(p)}"</li>`)
      .join('');
    notesItems.push(`<div style="line-height:1.45;"><div style="font-weight:600;color:#334155;margin-bottom:3px;">emphasis points:</div><ul style="margin:0 0 0 16px;padding:0;list-style:disc;">${emphasisList}</ul></div>`);
  }
  if (productionNotes.suggestedPacing) {
    notesItems.push(`<div style="line-height:1.45;"><span style="font-weight:600;color:#334155;">suggested pacing:</span> ${escapeHtml(productionNotes.suggestedPacing)}</div>`);
  }

  const notesHtml = notesItems.length > 0
    ? `<div style="padding:10px 12px;background:#f8fafc;border-radius:6px;border:1px solid #e2e8f0;">`
      + `<div style="font-weight:600;color:#059669;margin-bottom:8px;font-size:0.82rem;text-transform:lowercase;letter-spacing:0.03em;">production notes</div>`
      + `<div style="display:flex;flex-direction:column;gap:6px;font-size:0.84rem;color:${subtitle};">${notesItems.join('')}</div>`
      + `</div>`
    : '';

  return generateSpecialMessageTemplate({
    header: { title: escapeHtml(title), iconHtml: videoIconSvg },
    sections: [
      { type: 'custom', content: metadataHtml },
      { type: 'custom', content: scriptHtml },
      ...(notesHtml ? [{ type: 'custom' as const, content: notesHtml }] : []),
    ],
    buttons: [
      { label: 'translate to spanish', id: translateButtonId, background: PREST0_PRIMARY_CTA_BG },
    ],
    theme: 'green',
    sectionGap: '10px',
  });
}

/**
 * Format the video script success message with action buttons.
 */
export function formatVideoScriptSuccessHtml(
  title: string,
  scriptContent: string,
  copyButtonId: string
): string {
  const { accent, accentBg, subtitle } = VIDEO_SCRIPT_COLORS;

  // Escape HTML in script content for data attribute
  const escapeHtml = (text: string) =>
    text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  // Checkmark SVG icon
  const checkIconSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

  return `
    <div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:${accentBg};color:${accent};">${checkIconSvg}</span>
        <strong style="color:${accent};font-size:0.95rem;">your video script is ready</strong>
      </div>
      <span style="color:${subtitle};font-size:0.9rem;line-height:1.5;">"${escapeHtml(title)}" is ready to use with your HeyGen avatar.</span>
      <div style="display:flex;gap:8px;max-width:400px;margin-top:4px;">
        <button
          id="${copyButtonId}"
          data-script="${escapeHtml(scriptContent)}"
          style="display:inline-flex;align-items:center;justify-content:center;text-align:center;background:${accent};color:#fff;padding:8px 12px;border:none;border-radius:6px;font-weight:500;font-size:0.82rem;cursor:pointer;transition:all 0.15s ease;"
        >
          <span style="text-transform:lowercase;">copy script</span>
        </button>
      </div>
    </div>
  `;
}

// ============================================================================
// Special Message Template Generator
// ============================================================================

/**
 * Convert hex color to RGB string for use in rgba().
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`
    : '0,0,0';
}

/**
 * Escape HTML attribute value for safe inclusion in data attributes.
 */
function escapeHtmlAttribute(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Resolve color palette from theme name or custom colors.
 */
function resolveThemeColors(config: SpecialMessageTemplateConfig): SpecialMessageColorPalette {
  if (config.theme === 'custom' && config.customColors) {
    return config.customColors;
  }
  const theme = config.theme || 'blue';
  if (theme === 'custom') {
    return SPECIAL_MESSAGE_THEMES.blue;
  }
  return SPECIAL_MESSAGE_THEMES[theme];
}

/**
 * Render the header section with icon badge and title.
 * Supports multiple icon types: character, SVG, image URL, base64 image.
 * If no icon is provided, renders title only (no badge).
 */
function renderTemplateHeader(header: TemplateHeaderConfig, colors: SpecialMessageColorPalette): string {
  const titleStyle = `color:${colors.accent};font-size:0.95rem;`;

  // Determine icon content with priority: iconHtml > iconUrl > iconBase64 > icon
  let iconContent: string | null = null;
  let isImageIcon = false;

  if (header.iconHtml) {
    // Custom HTML/SVG (highest priority)
    iconContent = header.iconHtml;
  } else if (header.iconUrl) {
    // Image URL - render as circular image
    iconContent = `<img src="${header.iconUrl}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" alt="" />`;
    isImageIcon = true;
  } else if (header.iconBase64) {
    // Base64 image
    const mimeType = header.iconBase64Type || 'image/png';
    iconContent = `<img src="data:${mimeType};base64,${header.iconBase64}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" alt="" />`;
    isImageIcon = true;
  } else if (header.icon) {
    // Character/emoji (lowest priority)
    iconContent = header.icon;
  }

  // If no icon, render title only (single-line, no badge)
  if (!iconContent) {
    return `<div style="display:flex;align-items:center;">
      <strong style="${titleStyle}">${header.title}</strong>
    </div>`;
  }

  // Render with icon badge - use different styles for image vs character icons
  const badgeStyle = isImageIcon
    ? `display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;overflow:hidden;flex-shrink:0;`
    : `display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:${colors.accentBg};color:${colors.accent};font-weight:700;font-size:0.85rem;flex-shrink:0;`;

  return `<div style="display:flex;align-items:center;gap:8px;">
    <span style="${badgeStyle}">${iconContent}</span>
    <strong style="${titleStyle}">${header.title}</strong>
  </div>`;
}

/**
 * Render a text section with optional title.
 */
function renderTextSection(section: TemplateSectionConfig, colors: SpecialMessageColorPalette): string {
  const titleHtml = section.title
    ? `<strong style="color:${colors.accent};">${section.title}:</strong><br>`
    : '';
  const contentStyle = section.style?.fontSize
    ? `font-size:${section.style.fontSize};`
    : '';
  const customStyle = section.style?.custom || '';
  const content = typeof section.content === 'string' ? section.content : section.content.join('<br>');

  return `<div style="flex-shrink:0;color:#1C1917;${contentStyle}${customStyle}">${titleHtml}${content}</div>`;
}

/**
 * Render a boxed section with light background and optional scrolling.
 * When maxHeight is not specified, section expands to fit content (dynamic height).
 * When maxHeight is specified, section is scrollable with overflow-y:auto.
 */
function renderBoxedSection(section: TemplateSectionConfig, colors: SpecialMessageColorPalette): string {
  const bg = section.style?.background || 'rgba(198, 167, 94, 0.10)';
  const maxHeight = section.style?.maxHeight;  // No default - let content determine height naturally
  const fontSize = section.style?.fontSize || '0.9rem';
  const customStyle = section.style?.custom || '';

  const titleHtml = section.title
    ? `<strong>${section.title}:</strong><br/>`
    : '';
  const content = typeof section.content === 'string'
    ? section.content.replace(/\n/g, '<br/>')
    : section.content.join('<br/>');

  // Only apply max-height and overflow-y:auto when maxHeight is explicitly set
  // Otherwise use overflow:visible for dynamic height (prevents content overlap)
  const heightStyles = maxHeight
    ? `max-height:${maxHeight};overflow-y:auto;`
    : 'overflow:visible;';

  return `<div style="display:block;flex-shrink:0;padding:8px 10px;background:${bg};border-radius:6px;font-size:${fontSize};color:#1C1917;line-height:1.5;${heightStyles}position:relative;box-sizing:border-box;word-wrap:break-word;${customStyle}">${titleHtml}${content}</div>`;
}

/**
 * Render a list section with bulleted items.
 * IMPORTANT: No whitespace in template literals to prevent spacing issues.
 */
function renderListSection(section: TemplateSectionConfig, colors: SpecialMessageColorPalette): string {
  const items = Array.isArray(section.content) ? section.content : [section.content];
  const titleHtml = section.title
    ? `<strong style="color:${colors.accent};display:block;margin-bottom:4px;">${section.title}:</strong>`
    : '';
  const listHtml = items.map((item, index) =>
    `<li style="margin-bottom:${index === items.length - 1 ? '0' : '4px'};color:#1C1917;font-size:0.88rem;line-height:1.4;">${item}</li>`
  ).join('');

  // No whitespace between tags to prevent rendering issues
  return `<div style="flex-shrink:0;">${titleHtml}<ul style="margin:0;padding:0 0 0 18px;list-style-type:disc;list-style-position:outside;">${listHtml}</ul></div>`;
}

/**
 * Render a single section based on its type.
 */
function renderTemplateSection(section: TemplateSectionConfig, colors: SpecialMessageColorPalette): string {
  switch (section.type) {
    case 'text':
      return renderTextSection(section, colors);
    case 'boxed':
      return renderBoxedSection(section, colors);
    case 'list':
      return renderListSection(section, colors);
    case 'custom': {
      // IMPORTANT: Wrap custom content in a div to ensure it's a proper flex child
      // Without this wrapper, the flex container's gap property won't apply correctly
      // v6: Removed overflow:hidden which was clipping content inappropriately
      // v7: Added flex-shrink:0 to prevent section compression and overlap
      const customContent = typeof section.content === 'string' ? section.content : section.content.join('');
      return `<div style="display:block;flex-shrink:0;position:relative;box-sizing:border-box;">${customContent}</div>`;
    }
    default:
      return '';
  }
}

/**
 * Render a single button element.
 */
function renderTemplateButton(btn: TemplateButtonConfig, colors: SpecialMessageColorPalette): string {
  const bg = btn.background || colors.accent;
  const textColor = btn.textColor || '#fff';

  // Build data attributes string
  const dataAttrs = btn.dataAttributes
    ? Object.entries(btn.dataAttributes)
        .map(([key, value]) => `data-${key}="${escapeHtmlAttribute(value)}"`)
        .join(' ')
    : '';

  // Primary uses Stage 3 elevated styling, secondary uses consistent styling
  // width:100% ensures buttons stretch full width in vertical layout
  const buttonStyle = btn.variant === 'secondary'
    ? `display:inline-flex;align-items:center;justify-content:center;text-align:center;width:100%;background:rgba(198, 167, 94, 0.10);color:${colors.subtitle};padding:11px 16px;border:1px solid #E0D5BC;border-radius:12px;font-weight:700;font-size:0.82rem;cursor:pointer;transition:transform 120ms ease,box-shadow 120ms ease;`
    : `display:inline-flex;align-items:center;justify-content:center;text-align:center;width:100%;background:${bg};color:${textColor};padding:11px 16px;border:none;border-radius:12px;font-weight:700;cursor:pointer;box-shadow:0 12px 28px rgba(${hexToRgb(bg)},0.28);transition:transform 120ms ease,box-shadow 120ms ease;`;

  return `<button id="${btn.id}" ${dataAttrs} style="${buttonStyle}"><span style="text-transform:lowercase;">${btn.label}</span></button>`;
}

/**
 * Render the buttons container with all buttons.
 * Uses Stage 3 styling: more vertical spacing and optional separator line.
 * Default layout is 'vertical' (stacked) for consistency across workflows.
 */
function renderTemplateButtons(
  buttons: TemplateButtonConfig[],
  colors: SpecialMessageColorPalette,
  showSeparator: boolean = true,
  layout: 'vertical' | 'horizontal' = 'vertical'
): string {
  // For vertical layout, use narrower max-width; for horizontal, use wider based on button count
  const isVertical = layout === 'vertical';
  const maxWidth = isVertical ? '280px' : (buttons.length === 2 ? '450px' : '650px');

  const buttonsHtml = buttons.map(btn => renderTemplateButton(btn, colors)).join('');

  // Match Stage 3 styling: more vertical spacing, optional separator
  const separatorStyle = showSeparator ? 'border-top:1px solid #E0D5BC;' : '';

  // Use flex-direction based on layout preference
  const flexDirection = isVertical ? 'column' : 'row';

  return `<div style="width:100%;margin-top:20px;padding-top:16px;${separatorStyle}"><div style="display:flex;flex-direction:${flexDirection};flex-wrap:wrap;gap:10px;max-width:${maxWidth};">${buttonsHtml}</div></div>`;
}

/**
 * Render helper text at the bottom of the template.
 */
function renderTemplateHelperText(text: string, colors: SpecialMessageColorPalette): string {
  return `<p style="margin:4px 0 0;color:${colors.muted};font-size:0.85rem;">${text}</p>`;
}

/**
 * Generates HTML for a special message template.
 *
 * @param config - The template configuration
 * @returns HTML string ready for injection into chat messages
 *
 * @example
 * ```typescript
 * const html = generateSpecialMessageTemplate({
 *   header: { icon: '?', title: 'a few questions to focus your research' },
 *   sections: [
 *     { type: 'text', title: 'why deep research', content: 'Your query requires...' },
 *     { type: 'boxed', title: 'initial findings', content: 'We found that...' },
 *     { type: 'list', title: 'please consider', content: ['Question 1', 'Question 2'] },
 *   ],
 *   buttons: [{ label: 'proceed', id: 'proceed-btn' }],
 *   theme: 'blue',
 *   helperText: 'reply with your answers or type "skip" to proceed.',
 * });
 * ```
 */
export function generateSpecialMessageTemplate(config: SpecialMessageTemplateConfig): string {
  const colors = resolveThemeColors(config);
  const containerStyle = `display:flex;flex-direction:column;gap:${config.sectionGap || '8px'};margin:${config.containerMargin || '4px 0'};box-sizing:border-box;min-height:0;width:100%;isolation:isolate;`;

  let html = `<div style="${containerStyle}">`;

  // Render header
  html += renderTemplateHeader(config.header, colors);

  // Render sections
  if (config.sections) {
    for (const section of config.sections) {
      html += renderTemplateSection(section, colors);
    }
  }

  // Render helper text (before buttons if present)
  if (config.helperText) {
    html += renderTemplateHelperText(config.helperText, colors);
  }

  // Render buttons (with separator by default, unless explicitly disabled)
  // Default to vertical layout for consistency across workflows
  if (config.buttons && config.buttons.length > 0) {
    html += renderTemplateButtons(
      config.buttons,
      colors,
      config.showButtonSeparator !== false,
      config.buttonLayout || 'vertical'
    );
  }

  html += '</div>';

  return html;
}

// ============================================================================
// Workflow Action Buttons Generator
// ============================================================================

/**
 * Configuration for a workflow action button.
 */
export interface WorkflowActionButtonConfig {
  /** Button label text */
  label: string;
  /** Unique ID for event delegation */
  id: string;
  /** Background color (defaults to theme accent) */
  background?: string;
  /** Data attributes for context passing (e.g., workflow-output-id) */
  dataAttributes?: Record<string, string>;
}

/**
 * Options for generating workflow action buttons HTML.
 */
export interface WorkflowActionButtonsOptions {
  /**
   * Theme for button styling. Can be a theme name ('blue', 'green', 'purple', etc.)
   * or a custom color palette. Defaults to 'blue'.
   */
  theme?: SpecialMessageTheme | SpecialMessageColorPalette;
  /**
   * Whether to show the border-top separator above buttons.
   * Defaults to true.
   */
  showSeparator?: boolean;
  /**
   * Button layout direction: 'vertical' (stacked) or 'horizontal' (side-by-side).
   * Defaults to 'vertical'.
   */
  layout?: 'vertical' | 'horizontal';
}

/**
 * Generates HTML for action buttons to be appended to workflow output messages.
 * Uses the same elevated styling as Stage 3 buttons in showSpecialMessage.
 *
 * This is a standard utility for embedding action buttons in any workflow output.
 * Follows the same pattern used by deep research and format report workflows.
 *
 * @param buttons - Array of button configurations
 * @param options - Optional styling configuration (theme, separator)
 * @returns HTML string for the button container
 *
 * @example
 * // Generate buttons for a formatted report
 * const buttonsHtml = generateWorkflowActionButtonsHtml([
 *   { label: 'translate to spanish', id: 'translate-btn', background: PREST0_PRIMARY_CTA_BG },
 *   { label: 'write video script', id: 'video-btn', background: PREST0_SECONDARY_CTA_BG },
 * ], { theme: 'purple' });
 *
 * // Insert before closing tag
 * const finalHtml = contentHtml.replace(/<\/article>$/, buttonsHtml + '</article>');
 */
export function generateWorkflowActionButtonsHtml(
  buttons: WorkflowActionButtonConfig[],
  options?: WorkflowActionButtonsOptions
): string {
  if (!buttons || buttons.length === 0) return '';

  // Resolve theme colors
  const themeInput = options?.theme || 'blue';
  const colors: SpecialMessageColorPalette =
    typeof themeInput === 'string'
      ? SPECIAL_MESSAGE_THEMES[themeInput as Exclude<SpecialMessageTheme, 'custom'>] || SPECIAL_MESSAGE_THEMES.blue
      : themeInput;

  const showSeparator = options?.showSeparator !== false; // Default true
  const layout = options?.layout || 'vertical'; // Default vertical

  // Generate button elements with elevated primary styling
  const buttonsHtml = buttons.map(btn => {
    const bg = btn.background || colors.accent;
    const rgbColor = hexToRgb(bg);

    // Build data attributes string for generalized context passing
    const dataAttrs = btn.dataAttributes
      ? Object.entries(btn.dataAttributes)
          .map(([key, value]) => `data-${key}="${escapeHtmlAttribute(value)}"`)
          .join(' ')
      : '';

    // width:100% ensures buttons stretch full width in vertical layout
    return `<button id="${btn.id}" ${dataAttrs} style="display:inline-flex;align-items:center;justify-content:center;width:100%;background:${bg};color:#fff;padding:11px 16px;border:none;border-radius:12px;font-weight:700;cursor:pointer;box-shadow:0 12px 28px rgba(${rgbColor},0.28);transition:transform 120ms ease,box-shadow 120ms ease;"><span style="text-transform:lowercase;">${btn.label}</span></button>`;
  }).join('');

  // Layout: vertical uses flex-column with narrower width, horizontal uses flex-row
  const isVertical = layout === 'vertical';
  const maxWidth = isVertical ? '280px' : (buttons.length === 2 ? '450px' : '650px');
  const flexDirection = isVertical ? 'column' : 'row';

  // Container styling with optional separator
  const separatorStyle = showSeparator ? 'border-top:1px solid #E0D5BC;' : '';

  return `<div style="margin-top:20px;padding-top:16px;${separatorStyle}"><div style="display:flex;flex-direction:${flexDirection};gap:10px;max-width:${maxWidth};">${buttonsHtml}</div></div>`;
}

// Backward compatibility aliases
export type ResearchActionButtonConfig = WorkflowActionButtonConfig;
export const generateResearchActionButtonsHtml = generateWorkflowActionButtonsHtml;

// ============================================================================
// Workflow Output Extraction Helpers
// ============================================================================

/**
 * Extract the most recent workflow output from chat history.
 * Used to re-hydrate content when buttons are clicked or page refreshes.
 * Searches from most recent to oldest message.
 *
 * @param messages - Array of chat messages to search
 * @param type - Optional filter for specific workflow output type
 * @returns The most recent matching workflow output, or null if not found
 */
export function extractLatestWorkflowOutput(
  messages: ChatMessage[],
  type?: WorkflowOutputMetadata['type']
): WorkflowOutputMetadata | null {
  if (!messages || messages.length === 0) return null;

  // Search from most recent to oldest
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.workflowOutput) {
      if (!type || msg.workflowOutput.type === type) {
        return msg.workflowOutput;
      }
    }
  }
  return null;
}

/**
 * Extract workflow content from message text/HTML as a fallback.
 * Useful for extracting content from messages that don't have workflowOutput metadata
 * (e.g., older messages created before this feature was added).
 *
 * @param messages - Array of chat messages to search
 * @param sourceTagPattern - Regex pattern to match message sourceTag
 * @returns Object with content and title, or null if not found
 */
export function extractContentFromMessageHtml(
  messages: ChatMessage[],
  sourceTagPattern: RegExp
): { content: string; title: string } | null {
  if (!messages || messages.length === 0) return null;

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.sourceTag && sourceTagPattern.test(msg.sourceTag)) {
      // Prefer text content, fall back to stripping HTML
      const content = msg.text || stripHtmlTags(msg.html || '');
      if (content && content.length > 100) {
        // Try to extract title from first heading or first line
        const title = extractTitleFromContent(content);
        return { content, title };
      }
    }
  }
  return null;
}

/**
 * Strip HTML tags from a string.
 * Simple utility for extracting plain text from HTML content.
 */
function stripHtmlTags(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract a title from content by looking for headings or first meaningful line.
 */
function extractTitleFromContent(content: string): string {
  // Try to find markdown heading
  const headingMatch = content.match(/^#{1,3}\s+(.+?)$/m);
  if (headingMatch && headingMatch[1].length >= 5 && headingMatch[1].length <= 100) {
    return headingMatch[1].trim();
  }

  // Try to find bold title
  const boldMatch = content.match(/^\*\*([^*]+)\*\*/m);
  if (boldMatch && boldMatch[1].length >= 5 && boldMatch[1].length <= 100) {
    return boldMatch[1].trim();
  }

  // Fall back to first line (truncated)
  const firstLine = content.split('\n')[0].trim();
  if (firstLine.length > 50) {
    return firstLine.slice(0, 50) + '...';
  }
  return firstLine || 'Research Report';
}

/**
 * Generate a contextual initial context summary based on query keywords.
 * Used as fallback when AI fails to provide substantive preliminary findings.
 * This ensures the "initial findings" section is always query-relevant,
 * never generic canned text.
 */
export function generateQueryBasedInitialContext(query: string): string {
  const queryLower = query.toLowerCase();

  // Immigration topic detection patterns with topic-specific INA citations
  const topicPatterns: Array<{
    pattern: RegExp;
    topic: string;
    context: string;
  }> = [
    {
      pattern: /asylum|persecution|refugee|fear|country\s+conditions|credible\s+fear/i,
      topic: 'asylum',
      context: `This query involves asylum law under INA § 208. Key legal framework includes demonstrating persecution or well-founded fear based on race, religion, nationality, political opinion, or membership in a particular social group. Recent precedent (Matter of A-B-, Matter of L-E-A-) has shaped particular social group analysis. The one-year filing deadline (with exceptions) and corroborating evidence requirements are critical procedural considerations.`,
    },
    {
      pattern: /u[\s-]?visa|crime\s+victim|substantial\s+abuse|i-918|certification/i,
      topic: 'u-visa',
      context: `This query involves U nonimmigrant status under INA § 101(a)(15)(U). Key requirements include victim of qualifying criminal activity, substantial physical or mental abuse, possession of useful information about the crime, and law enforcement certification (Form I-918B). There is an annual cap of 10,000 U visas, creating significant backlogs and waitlist considerations.`,
    },
    {
      pattern: /removal|deportation|proceedings|immigration\s+court|immigration\s+judge|nta|notice\s+to\s+appear/i,
      topic: 'removal',
      context: `This query involves removal proceedings under INA § 240. Key considerations include potential forms of relief (asylum, cancellation of removal, adjustment, voluntary departure), procedural rights, motion practice, and appeal options to the BIA. Venue-specific practices and individual immigration judge statistics may be relevant to strategy.`,
    },
    {
      pattern: /adjustment|green\s+card|permanent\s+residen|i-485|status\s+adjustment/i,
      topic: 'adjustment',
      context: `This query involves adjustment of status to lawful permanent residence under INA § 245. Key considerations include eligibility requirements, inadmissibility grounds and waivers, visa availability, and the distinction between immediate relatives and preference categories. Recent policy changes may affect processing and interview requirements.`,
    },
    {
      pattern: /work\s+permit|ead|employment\s+authorization|i-765|work\s+authorization/i,
      topic: 'ead',
      context: `This query involves employment authorization under 8 CFR 274a.12. Key considerations include eligibility category (asylum pending, adjustment pending, TPS, DACA, etc.), processing times, automatic extensions under certain categories, and renewal timing. Recent policy changes have affected automatic extension periods.`,
    },
    {
      pattern: /naturalization|citizenship|n-400|civics|oath\s+ceremony/i,
      topic: 'naturalization',
      context: `This query involves naturalization under INA § 316-318. Key requirements include continuous residence (typically 5 years, 3 years for spouses of USC), physical presence, good moral character, English and civics testing, and attachment to the Constitution. Recent USCIS guidance may affect specific requirements or exceptions.`,
    },
    {
      pattern: /visa\s+petition|i-130|family|spouse|parent|child|sibling/i,
      topic: 'family-visa',
      context: `This query involves family-based immigration under INA § 201-203. Key considerations include the relationship category (immediate relative vs preference), priority dates and visa bulletin timing, qualifying relationships, and potential inadmissibility issues. Processing times vary significantly by category and consulate.`,
    },
    {
      pattern: /h-1b|specialty\s+occupation|employer|sponsor|labor|prevailing\s+wage/i,
      topic: 'h1b',
      context: `This query involves H-1B nonimmigrant status under INA § 101(a)(15)(H)(i)(b). Key considerations include specialty occupation requirements, wage level determination, the annual cap and lottery system, employer-employee relationship, and potential for dual intent. Recent policy changes have affected adjudication standards.`,
    },
    {
      pattern: /tps|temporary\s+protected\s+status|country\s+designation/i,
      topic: 'tps',
      context: `This query involves Temporary Protected Status under INA § 244. Key considerations include country designation status, registration periods, continuous physical presence requirements, and eligibility for travel authorization and EAD. Recent court decisions and policy changes have affected TPS availability and extensions.`,
    },
    {
      pattern: /daca|dreamer|childhood\s+arrival|deferred\s+action/i,
      topic: 'daca',
      context: `This query involves Deferred Action for Childhood Arrivals (DACA). Key considerations include initial eligibility requirements, renewal procedures, advance parole limitations, and the program's current legal status following court challenges. Employment authorization and travel considerations are critical components.`,
    },
    {
      pattern: /cancellation\s+of\s+removal|10\s+year|exceptional.*hardship/i,
      topic: 'cancellation',
      context: `This query involves cancellation of removal under INA § 240A. For non-LPRs, key requirements include 10 years continuous physical presence, good moral character, and exceptional and extremely unusual hardship to a USC or LPR spouse, parent, or child. For LPRs, requirements include 7 years residence and 5 years as LPR without aggravated felony conviction.`,
    },
    {
      pattern: /waiver|i-601|unlawful\s+presence|inadmissibility|extreme\s+hardship/i,
      topic: 'waiver',
      context: `This query involves inadmissibility waivers. Key considerations include the specific ground of inadmissibility (unlawful presence under INA § 212(a)(9)(B), fraud/misrepresentation under INA § 212(a)(6)(C)), qualifying relatives for hardship analysis, and the applicable hardship standard. Processing locations and timelines vary by waiver type.`,
    },
  ];

  // Find matching topic
  for (const { pattern, context } of topicPatterns) {
    if (pattern.test(queryLower)) {
      return `${context}\n\nTo focus this research effectively:`;
    }
  }

  // Default contextual summary if no specific topic matched
  return `Based on your query, this involves immigration law provisions requiring analysis of eligibility requirements, procedural considerations, and applicable regulations. Key areas typically include statutory requirements under the Immigration and Nationality Act (INA), implementing regulations in 8 CFR, and relevant USCIS policy guidance.\n\nTo focus this research effectively:`;
}
